/** @format */

import React, {
  Suspense,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { motion, useAnimation } from "framer-motion";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Loader,
} from "@react-three/drei";
import {
  Car,
  User,
  Clipboard,
  Wrench,
  CheckSquare,
  Save,
  X,
  Eye,
  Search,
  Loader as LoaderIcon,
  UserPlus,
  Check,
  List,
  CheckCircle,
  ArrowLeft,
  Camera,
  Upload,
  ChevronDown,
  ChevronRight,
  Users,
  Calendar,
  BookOpen,
  Clock,
  Plus,
  Trash2,
  Edit3,
  CalendarDays,
  Layers,
  BarChart3,
  History,
} from "lucide-react";
import { supabase } from "@lib/supabaseClient";
import { useNavigate, useLocation } from "react-router-dom";
import ReceptionForm from "./ReceptionForm";
import ReceptionSearch from "./ReceptionSearch";

// ✅ Fonction utilitaire pour nettoyer les caractères mal encodés
const cleanText = (text) => {
  if (!text) return "";

  const replacements = {
    "Ã©": "é",
    "Ã¨": "è",
    Ãª: "ê",
    "Ã ": "à",
    "Ã§": "ç",
    "Ã´": "ô",
    "Ã®": "î",
    "Ã¯": "ï",
    "Ã¹": "ù",
    "Ã»": "û",
    "Ã¼": "ü",
    "Ã‰": "É",
    Ãˆ: "È",
    ÃŠ: "Ê",
    "Ã€": "À",
    "Ã‡": "Ç",
    'Ã"': "Ô",
    "Ã�": "é",
    "�": "é",
  };

  let cleaned = text;
  for (const [wrong, correct] of Object.entries(replacements)) {
    cleaned = cleaned.replace(new RegExp(wrong, "g"), correct);
  }

  return cleaned;
};

// ✅ Fonction pour obtenir une URL signée (sécurisée, expire après 1h)
const getSignedUrl = async (bucket, filePath) => {
  if (!filePath) return null;

  // Si c'est un base64, le retourner directement
  if (typeof filePath === "string" && filePath.startsWith("data:"))
    return filePath;

  // Extraire le nom du fichier si c'est une URL complète
  let fileName = filePath;
  if (
    typeof filePath === "string" &&
    filePath.includes("/storage/v1/object/")
  ) {
    const parts = filePath.split("/");
    fileName = parts[parts.length - 1].split("?")[0];
  }

  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(fileName, 3600); // Expire dans 1 heure

    if (error) {
      console.error(
        `❌ Erreur création URL signée pour ${bucket}/${fileName}:`,
        error
      );
      return null;
    }

    console.log(`✅ URL signée créée pour ${bucket}/${fileName}`);
    return data.signedUrl;
  } catch (err) {
    console.error(`❌ Exception lors de la création URL signée:`, err);
    return null;
  }
};

// ✅ Fonction pour charger plusieurs URLs signées en parallèle
const getSignedUrls = async (bucket, filePaths) => {
  const results = {};

  const promises = Object.entries(filePaths).map(async ([key, path]) => {
    if (path) {
      results[key] = await getSignedUrl(bucket, path);
    }
  });

  await Promise.all(promises);
  return results;
};

// ✅ Composant de signature personnalisé optimisé
const OptimizedSignaturePad = React.forwardRef(
  ({ penColor = "black", backgroundColor = "white", onEnd }, ref) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const isDrawing = useRef(false);
    const lastPoint = useRef(null);
    const paths = useRef([]);
    const currentPath = useRef([]);
    const isInitialized = useRef(false);

    // Exposer les méthodes via ref
    React.useImperativeHandle(ref, () => ({
      clear: () => {
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          paths.current = [];
          currentPath.current = [];
        }
      },
      isEmpty: () => {
        return paths.current.length === 0;
      },
      toDataURL: (type = "image/png") => {
        return canvasRef.current?.toDataURL(type) || "";
      },
    }));

    const redrawPaths = useCallback(
      (ctx, width, height) => {
        // Fond blanc
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, width, height);

        // Redessiner tous les chemins
        ctx.strokeStyle = penColor;
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        paths.current.forEach((path) => {
          if (path.length < 2) return;
          ctx.beginPath();
          ctx.moveTo(path[0].x, path[0].y);
          for (let i = 1; i < path.length; i++) {
            ctx.lineTo(path[i].x, path[i].y);
          }
          ctx.stroke();
        });
      },
      [penColor, backgroundColor]
    );

    const setupCanvas = useCallback(() => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;

      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      // Définir la taille du canvas
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;

      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);

      // Redessiner le contenu existant
      redrawPaths(ctx, rect.width, rect.height);

      isInitialized.current = true;
    }, [redrawPaths]);

    useEffect(() => {
      setupCanvas();

      let resizeTimeout;
      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          setupCanvas();
        }, 100);
      };

      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
        clearTimeout(resizeTimeout);
      };
    }, [setupCanvas]);

    const getCoordinates = (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return null;

      const rect = canvas.getBoundingClientRect();
      let clientX, clientY;

      if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else if (e.changedTouches && e.changedTouches.length > 0) {
        clientX = e.changedTouches[0].clientX;
        clientY = e.changedTouches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    };

    const startDrawing = (e) => {
      e.preventDefault();
      e.stopPropagation();

      isDrawing.current = true;
      const point = getCoordinates(e);
      if (point) {
        lastPoint.current = point;
        currentPath.current = [point];
      }
    };

    const draw = (e) => {
      if (!isDrawing.current) return;
      e.preventDefault();
      e.stopPropagation();

      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      const point = getCoordinates(e);

      if (point && lastPoint.current) {
        ctx.strokeStyle = penColor;
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        ctx.beginPath();
        ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
        ctx.lineTo(point.x, point.y);
        ctx.stroke();

        lastPoint.current = point;
        currentPath.current.push(point);
      }
    };

    const stopDrawing = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (isDrawing.current && currentPath.current.length > 0) {
        paths.current.push([...currentPath.current]);
        currentPath.current = [];
        if (onEnd) onEnd();
      }
      isDrawing.current = false;
      lastPoint.current = null;
    };

    return (
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "150px",
          position: "relative",
          backgroundColor: backgroundColor,
          borderRadius: "0.5rem",
          overflow: "hidden",
        }}>
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            touchAction: "none",
            cursor: "crosshair",
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          onTouchCancel={stopDrawing}
        />
      </div>
    );
  }
);

OptimizedSignaturePad.displayName = "OptimizedSignaturePad";

// Préchargement du modèle
useGLTF.preload("/models/2014_lamborghini_veneno_roadster.glb");

function Model() {
  const { scene } = useGLTF("/models/2014_lamborghini_veneno_roadster.glb");
  return (
    <Center>
      <primitive
        object={scene}
        scale={[0.01, 0.01, 0.01]}
        position={[0, -1, 0]}
        rotation={[0, Math.PI / 4, 0]}
      />
    </Center>
  );
}

function CarScene() {
  const [contextLost, setContextLost] = useState(false);

  useEffect(() => {
    const handleContextLost = () => {
      console.log("WebGL context lost, attempting to recover...");
      setContextLost(true);
      setTimeout(() => setContextLost(false), 2000);
    };

    window.addEventListener("webglcontextlost", handleContextLost);
    return () =>
      window.removeEventListener("webglcontextlost", handleContextLost);
  }, []);

  if (contextLost) {
    return <div className='fixed inset-0 bg-slate-900 opacity-30'></div>;
  }

  return (
    <Canvas
      shadows
      camera={{ position: [6, 3, 6], fov: 45 }}
      className='fixed inset-0 opacity-30'
      onCreated={({ gl }) => {
        gl.powerPreference = "high-performance";
        gl.antialias = false;
      }}>
      <color attach='background' args={["#1e1e2e"]} />
      <Suspense fallback={null}>
        <Stage environment='city'>
          <Model />
        </Stage>
        <Environment preset='city' />
        <OrbitControls autoRotate />
      </Suspense>
    </Canvas>
  );
}

// ✅ CompetenceSelector avec Pôles → Blocs → Activités → Tâches
const CompetenceSelector = ({
  typeFormation,
  selectedCompetences,
  onCompetenceChange,
}) => {
  const [poles, setPoles] = useState([]);
  const [blocs, setBlocs] = useState({});
  const [activites, setActivites] = useState({});
  const [taches, setTaches] = useState({});

  const [expandedPoles, setExpandedPoles] = useState({});
  const [expandedBlocs, setExpandedBlocs] = useState({});
  const [expandedActivites, setExpandedActivites] = useState({});

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (typeFormation) {
      loadCompetences();
    } else {
      setPoles([]);
      setBlocs({});
      setActivites({});
      setTaches({});
      setLoading(false);
    }
  }, [typeFormation]);

  const loadCompetences = async () => {
    if (!typeFormation) return;

    setLoading(true);
    try {
      console.log("📚 Chargement compétences pour:", typeFormation);

      const { data: polesData, error: polesError } = await supabase
        .from("poles")
        .select("*")
        .eq("type_formation", typeFormation)
        .order("ordre");

      if (polesError) throw polesError;

      if (!polesData || polesData.length === 0) {
        console.warn("⚠️ Aucun pôle trouvé pour:", typeFormation);
        setPoles([]);
        setLoading(false);
        return;
      }

      setPoles(polesData);
      setExpandedPoles({ [polesData[0].id]: true });

      const poleIds = polesData.map((p) => p.id);

      const { data: blocsData, error: blocsError } = await supabase
        .from("blocs")
        .select("*")
        .in("pole_id", poleIds)
        .order("ordre");

      if (blocsError) throw blocsError;

      const blocsGrouped = (blocsData || []).reduce((acc, bloc) => {
        if (!acc[bloc.pole_id]) acc[bloc.pole_id] = [];
        acc[bloc.pole_id].push(bloc);
        return acc;
      }, {});
      setBlocs(blocsGrouped);

      if (blocsData && blocsData.length > 0) {
        const blocIds = blocsData.map((b) => b.id);

        const { data: activitesData, error: activitesError } = await supabase
          .from("activites")
          .select("*")
          .in("bloc_id", blocIds)
          .order("ordre");

        if (activitesError) throw activitesError;

        const activitesGrouped = (activitesData || []).reduce(
          (acc, activite) => {
            if (!acc[activite.bloc_id]) acc[activite.bloc_id] = [];
            acc[activite.bloc_id].push(activite);
            return acc;
          },
          {}
        );
        setActivites(activitesGrouped);

        if (activitesData && activitesData.length > 0) {
          const activiteIds = activitesData.map((a) => a.id);

          const { data: tachesData, error: tachesError } = await supabase
            .from("taches")
            .select("*")
            .in("activite_id", activiteIds)
            .order("ordre");

          if (tachesError) throw tachesError;

          const tachesGrouped = (tachesData || []).reduce((acc, tache) => {
            if (!acc[tache.activite_id]) acc[tache.activite_id] = [];
            acc[tache.activite_id].push(tache);
            return acc;
          }, {});
          setTaches(tachesGrouped);
        }
      }

      console.log("✅ Compétences chargées avec succès");
    } catch (error) {
      console.error("❌ Erreur chargement compétences:", error);
    } finally {
      setLoading(false);
    }
  };

  const togglePole = (poleId) => {
    setExpandedPoles((prev) => ({ ...prev, [poleId]: !prev[poleId] }));
  };

  const toggleBloc = (blocId) => {
    setExpandedBlocs((prev) => ({ ...prev, [blocId]: !prev[blocId] }));
  };

  const toggleActivite = (activiteId) => {
    setExpandedActivites((prev) => ({
      ...prev,
      [activiteId]: !prev[activiteId],
    }));
  };

  const handleTacheCheck = (pole, bloc, activite, tache) => {
    const isSelected = selectedCompetences.some(
      (sel) => sel.tache_id === tache.id
    );

    if (isSelected) {
      onCompetenceChange(
        selectedCompetences.filter((sel) => sel.tache_id !== tache.id)
      );
    } else {
      onCompetenceChange([
        ...selectedCompetences,
        {
          pole_id: pole.id,
          pole_code: pole.code,
          pole_titre: cleanText(pole.titre),
          bloc_id: bloc.id,
          bloc_code: bloc.code,
          bloc_titre: cleanText(bloc.titre),
          activite_id: activite.id,
          activite_code: activite.code,
          activite_titre: cleanText(activite.titre),
          tache_id: tache.id,
          tache_code: tache.code,
          tache_titre: cleanText(tache.titre),
          niveau: "découverte", // ✅ Niveau par défaut
        },
      ]);
    }
  };

  const handleActiviteCheck = (pole, bloc, activite) => {
    const tachesActivite = taches[activite.id] || [];

    if (tachesActivite.length === 0) {
      const isSelected = selectedCompetences.some(
        (sel) => sel.activite_id === activite.id && !sel.tache_id
      );

      if (isSelected) {
        onCompetenceChange(
          selectedCompetences.filter(
            (sel) => !(sel.activite_id === activite.id && !sel.tache_id)
          )
        );
      } else {
        onCompetenceChange([
          ...selectedCompetences,
          {
            pole_id: pole.id,
            pole_code: pole.code,
            pole_titre: cleanText(pole.titre),
            bloc_id: bloc.id,
            bloc_code: bloc.code,
            bloc_titre: cleanText(bloc.titre),
            activite_id: activite.id,
            activite_code: activite.code,
            activite_titre: cleanText(activite.titre),
            tache_id: null,
            tache_code: null,
            tache_titre: null,
            niveau: "découverte", // ✅ Niveau par défaut
          },
        ]);
      }
      return;
    }

    const allSelected = tachesActivite.every((t) =>
      selectedCompetences.some((sel) => sel.tache_id === t.id)
    );

    if (allSelected) {
      onCompetenceChange(
        selectedCompetences.filter(
          (sel) => !tachesActivite.some((t) => t.id === sel.tache_id)
        )
      );
    } else {
      const newSelections = tachesActivite
        .filter(
          (t) => !selectedCompetences.some((sel) => sel.tache_id === t.id)
        )
        .map((tache) => ({
          pole_id: pole.id,
          pole_code: pole.code,
          pole_titre: cleanText(pole.titre),
          bloc_id: bloc.id,
          bloc_code: bloc.code,
          bloc_titre: cleanText(bloc.titre),
          activite_id: activite.id,
          activite_code: activite.code,
          activite_titre: cleanText(activite.titre),
          tache_id: tache.id,
          tache_code: tache.code,
          tache_titre: cleanText(tache.titre),
          niveau: "découverte", // ✅ Niveau par défaut
        }));

      onCompetenceChange([...selectedCompetences, ...newSelections]);
    }
  };

  const getActiviteSelectionState = (activiteId) => {
    const tachesActivite = taches[activiteId] || [];

    if (tachesActivite.length === 0) {
      return selectedCompetences.some((sel) => sel.activite_id === activiteId) ?
          "all"
        : "none";
    }

    const selectedCount = tachesActivite.filter((t) =>
      selectedCompetences.some((sel) => sel.tache_id === t.id)
    ).length;

    if (selectedCount === 0) return "none";
    if (selectedCount === tachesActivite.length) return "all";
    return "partial";
  };

  if (!typeFormation) {
    return (
      <div className='bg-slate-700/50 p-6 rounded-lg text-center'>
        <BookOpen className='w-12 h-12 text-slate-400 mx-auto mb-3' />
        <p className='text-gray-400'>
          Sélectionnez d'abord un groupe pour afficher les compétences
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className='bg-slate-700/50 p-6 rounded-lg text-center'>
        <LoaderIcon className='w-8 h-8 text-blue-400 mx-auto mb-3 animate-spin' />
        <p className='text-gray-400'>Chargement des compétences...</p>
      </div>
    );
  }

  if (poles.length === 0) {
    return (
      <div className='bg-orange-500/20 border border-orange-500 rounded-lg p-6 text-center'>
        <BookOpen className='w-12 h-12 text-orange-400 mx-auto mb-3' />
        <p className='text-orange-400'>
          Aucune compétence trouvée pour "{typeFormation}"
        </p>
        <p className='text-gray-400 text-sm mt-2'>
          Veuillez d'abord créer le référentiel dans "Gestion des Compétences"
        </p>
      </div>
    );
  }

  return (
    <div className='bg-slate-700/50 p-4 rounded-lg max-h-96 overflow-y-auto'>
      <p className='text-sm text-gray-300 mb-4'>
        Sélectionnez les compétences travaillées lors de cette intervention
      </p>

      <div className='space-y-2'>
        {poles.map((pole) => (
          <div
            key={pole.id}
            className='border border-slate-600 rounded-lg overflow-hidden'>
            <button
              type='button'
              onClick={() => togglePole(pole.id)}
              className='w-full flex items-center gap-2 p-3 bg-blue-600/20 hover:bg-blue-600/30 transition-colors text-left'>
              {expandedPoles[pole.id] ?
                <ChevronDown className='w-4 h-4 text-blue-400 flex-shrink-0' />
              : <ChevronRight className='w-4 h-4 text-blue-400 flex-shrink-0' />
              }
              <span className='text-blue-400 font-mono text-sm font-bold'>
                {pole.code}
              </span>
              <span className='text-white font-medium text-sm flex-1'>
                {cleanText(pole.titre)}
              </span>
            </button>

            {expandedPoles[pole.id] && (
              <div className='p-2 space-y-2 bg-slate-800/50'>
                {(blocs[pole.id] || []).map((bloc) => (
                  <div
                    key={bloc.id}
                    className='border border-slate-500 rounded'>
                    <button
                      type='button'
                      onClick={() => toggleBloc(bloc.id)}
                      className='w-full flex items-center gap-2 p-2 bg-green-600/10 hover:bg-green-600/20 transition-colors text-left'>
                      {expandedBlocs[bloc.id] ?
                        <ChevronDown className='w-4 h-4 text-green-400 flex-shrink-0' />
                      : <ChevronRight className='w-4 h-4 text-green-400 flex-shrink-0' />
                      }
                      <span className='text-green-400 font-mono text-xs font-semibold'>
                        {bloc.code}
                      </span>
                      <span className='text-white text-sm flex-1'>
                        {cleanText(bloc.titre)}
                      </span>
                    </button>

                    {expandedBlocs[bloc.id] && (
                      <div className='p-2 space-y-1 bg-slate-700/50'>
                        {(activites[bloc.id] || []).map((activite) => {
                          const selectionState = getActiviteSelectionState(
                            activite.id
                          );
                          const tachesActivite = taches[activite.id] || [];

                          return (
                            <div
                              key={activite.id}
                              className='border border-slate-400 rounded'>
                              <div className='flex items-center gap-2 p-2 bg-yellow-600/10'>
                                <input
                                  type='checkbox'
                                  checked={selectionState === "all"}
                                  ref={(input) => {
                                    if (input)
                                      input.indeterminate =
                                        selectionState === "partial";
                                  }}
                                  onChange={() =>
                                    handleActiviteCheck(pole, bloc, activite)
                                  }
                                  className='h-4 w-4 text-yellow-600 rounded cursor-pointer flex-shrink-0'
                                />
                                <button
                                  type='button'
                                  onClick={() => toggleActivite(activite.id)}
                                  className='flex items-center gap-2 flex-1 text-left'>
                                  {tachesActivite.length > 0 ?
                                    expandedActivites[activite.id] ?
                                      <ChevronDown className='w-3 h-3 text-yellow-400' />
                                    : <ChevronRight className='w-3 h-3 text-yellow-400' />

                                  : <span className='w-3' />}
                                  <span className='text-yellow-400 font-mono text-xs'>
                                    {activite.code}
                                  </span>
                                  <span className='text-white text-xs flex-1'>
                                    {cleanText(activite.titre)}
                                  </span>
                                  {tachesActivite.length > 0 && (
                                    <span className='text-gray-400 text-xs'>
                                      (
                                      {
                                        tachesActivite.filter((t) =>
                                          selectedCompetences.some(
                                            (sel) => sel.tache_id === t.id
                                          )
                                        ).length
                                      }
                                      /{tachesActivite.length})
                                    </span>
                                  )}
                                </button>
                              </div>

                              {expandedActivites[activite.id] &&
                                tachesActivite.length > 0 && (
                                  <div className='p-2 space-y-1 bg-slate-600/30'>
                                    {tachesActivite.map((tache) => {
                                      const isSelected =
                                        selectedCompetences.some(
                                          (sel) => sel.tache_id === tache.id
                                        );

                                      return (
                                        <label
                                          key={tache.id}
                                          className={`flex items-center gap-2 p-1.5 rounded cursor-pointer transition-colors ${
                                            isSelected ?
                                              "bg-purple-500/20 border border-purple-500/50"
                                            : "hover:bg-slate-600/50"
                                          }`}>
                                          <input
                                            type='checkbox'
                                            checked={isSelected}
                                            onChange={() =>
                                              handleTacheCheck(
                                                pole,
                                                bloc,
                                                activite,
                                                tache
                                              )
                                            }
                                            className='h-3.5 w-3.5 text-purple-600 rounded cursor-pointer flex-shrink-0'
                                          />
                                          <span className='text-purple-400 font-mono text-xs'>
                                            {tache.code}
                                          </span>
                                          <span
                                            className={`text-xs ${isSelected ? "text-white" : "text-gray-300"}`}>
                                            {cleanText(tache.titre)}
                                          </span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                            </div>
                          );
                        })}

                        {(!activites[bloc.id] ||
                          activites[bloc.id].length === 0) && (
                          <p className='text-gray-500 text-xs text-center py-2'>
                            Aucune activité dans ce bloc
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {(!blocs[pole.id] || blocs[pole.id].length === 0) && (
                  <p className='text-gray-500 text-sm text-center py-2'>
                    Aucun bloc dans ce pôle
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedCompetences.length > 0 && (
        <div className='mt-4 pt-4 border-t border-slate-600'>
          <p className='text-sm font-medium text-blue-400 mb-2'>
            {selectedCompetences.length} compétence(s) sélectionnée(s)
          </p>
          <div className='flex flex-wrap gap-1'>
            {selectedCompetences.slice(0, 5).map((comp, idx) => (
              <span
                key={idx}
                className='bg-purple-500/20 text-purple-300 text-xs px-2 py-1 rounded'>
                {comp.tache_code || comp.activite_code}
              </span>
            ))}
            {selectedCompetences.length > 5 && (
              <span className='bg-slate-600 text-gray-300 text-xs px-2 py-1 rounded'>
                +{selectedCompetences.length - 5} autres
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ✅ Composant ApprenantSelector avec nettoyage UTF-8
const ApprenantSelector = ({ onSelect, selectedName, groupe }) => {
  const [apprenants, setApprenants] = useState([]);
  const [filteredApprenants, setFilteredApprenants] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadApprenants();
  }, [groupe]);

  const loadApprenants = async () => {
    setLoading(true);
    try {
      let query = supabase.from("apprenants").select("*").order("nom");

      if (groupe) {
        query = query.eq("groupe", groupe);
      }

      const { data, error } = await query;

      if (error) throw error;

      const cleanedData = (data || []).map((apprenant) => ({
        ...apprenant,
        nom: cleanText(apprenant.nom),
        prenom: cleanText(apprenant.prenom),
      }));

      setApprenants(cleanedData);
      setFilteredApprenants(cleanedData);
    } catch (error) {
      console.error("Erreur lors du chargement des apprenants:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setShowDropdown(true);

    if (!value.trim()) {
      setFilteredApprenants(apprenants);
      return;
    }

    const filtered = apprenants.filter(
      (app) =>
        `${app.prenom} ${app.nom}`
          .toLowerCase()
          .includes(value.toLowerCase()) ||
        app.nom.toLowerCase().includes(value.toLowerCase()) ||
        app.prenom.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredApprenants(filtered);
  };

  const handleSelect = (apprenant) => {
    const fullName = `${apprenant.prenom} ${apprenant.nom}`;
    onSelect(fullName, apprenant.id);
    setSearchTerm(fullName);
    setShowDropdown(false);
  };

  useEffect(() => {
    if (selectedName) {
      setSearchTerm(selectedName);
    }
  }, [selectedName]);

  return (
    <div className='relative'>
      <div className='relative'>
        <Users className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
        <input
          type='text'
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          placeholder='Rechercher ou saisir un nom...'
          className='w-full bg-slate-700 border border-slate-600 rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500'
        />
      </div>

      {showDropdown && (
        <>
          <div
            className='fixed inset-0 z-10'
            onClick={() => setShowDropdown(false)}
          />
          <div className='absolute z-20 w-full mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg max-h-60 overflow-y-auto'>
            {loading ?
              <div className='p-4 text-center text-gray-400'>
                <LoaderIcon className='w-5 h-5 animate-spin mx-auto mb-2' />
                Chargement...
              </div>
            : filteredApprenants.length > 0 ?
              <div className='py-1'>
                {filteredApprenants.map((apprenant) => (
                  <button
                    key={apprenant.id}
                    type='button'
                    onClick={() => handleSelect(apprenant)}
                    className='w-full text-left px-4 py-2 hover:bg-slate-600 text-white transition-colors'>
                    <div className='font-medium'>
                      {apprenant.prenom} {apprenant.nom}
                    </div>
                    <div className='text-sm text-gray-400'>
                      {apprenant.groupe}
                    </div>
                  </button>
                ))}
              </div>
            : <div className='p-4 text-center text-gray-400'>
                {searchTerm ?
                  "Aucun apprenant trouvé"
                : "Saisissez un nom pour rechercher"}
              </div>
            }
          </div>
        </>
      )}
    </div>
  );
};

// ✅ Composant pour gérer une session de travail
const SessionForm = ({
  session,
  index,
  onUpdate,
  onRemove,
  trainers,
  groupes,
  canRemove,
  isExpanded,
  onToggleExpand,
}) => {
  const [localSession, setLocalSession] = useState(session);

  useEffect(() => {
    setLocalSession(session);
  }, [session]);

  const handleChange = (field, value) => {
    const updated = { ...localSession, [field]: value };
    setLocalSession(updated);
    onUpdate(index, updated);
  };

  const handleApprenantChange = (
    appIndex,
    field,
    value,
    apprenantId = null
  ) => {
    const updatedApprenants = [...(localSession.apprenants_presents || [])];
    updatedApprenants[appIndex] = {
      ...updatedApprenants[appIndex],
      [field]: value,
    };
    if (apprenantId) {
      updatedApprenants[appIndex].apprenantId = apprenantId;
    }
    if (field === "groupe") {
      updatedApprenants[appIndex].competences = [];
    }
    handleChange("apprenants_presents", updatedApprenants);
  };

  const handleApprenantCompetencesChange = (appIndex, competences) => {
    const updatedApprenants = [...(localSession.apprenants_presents || [])];
    updatedApprenants[appIndex] = {
      ...updatedApprenants[appIndex],
      competences: competences,
    };
    handleChange("apprenants_presents", updatedApprenants);
  };

  const addApprenant = () => {
    const updatedApprenants = [
      ...(localSession.apprenants_presents || []),
      {
        id: Date.now(),
        name: "",
        groupe: "",
        apprenantId: null,
        signature: null,
        competences: [],
      },
    ];
    handleChange("apprenants_presents", updatedApprenants);
  };

  const removeApprenant = (appIndex) => {
    if ((localSession.apprenants_presents || []).length > 1) {
      const updatedApprenants = localSession.apprenants_presents.filter(
        (_, i) => i !== appIndex
      );
      handleChange("apprenants_presents", updatedApprenants);
    }
  };

  const calculateSessionDuration = () => {
    if (!localSession.heure_debut || !localSession.heure_fin) return null;

    const [startH, startM] = localSession.heure_debut.split(":").map(Number);
    const [endH, endM] = localSession.heure_fin.split(":").map(Number);

    let diffMinutes = endH * 60 + endM - (startH * 60 + startM);
    if (diffMinutes < 0) diffMinutes += 24 * 60;

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    return hours > 0 ?
        `${hours}h${minutes > 0 ? ` ${minutes}min` : ""}`
      : `${minutes}min`;
  };

  return (
    <div className='border border-slate-600 rounded-lg overflow-hidden mb-4'>
      <div
        className='flex items-center justify-between p-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 cursor-pointer'
        onClick={onToggleExpand}>
        <div className='flex items-center gap-3'>
          {isExpanded ?
            <ChevronDown className='w-5 h-5 text-blue-400' />
          : <ChevronRight className='w-5 h-5 text-blue-400' />}
          <div className='flex items-center gap-2'>
            <CalendarDays className='w-5 h-5 text-blue-400' />
            <span className='font-semibold text-white'>
              Session #{index + 1}
            </span>
          </div>
          {localSession.date_session && (
            <span className='text-sm text-gray-400'>
              -{" "}
              {new Date(localSession.date_session).toLocaleDateString("fr-FR", {
                weekday: "short",
                day: "numeric",
                month: "short",
              })}
            </span>
          )}
          {calculateSessionDuration() && (
            <span className='bg-blue-500/20 text-blue-300 text-xs px-2 py-1 rounded'>
              {calculateSessionDuration()}
            </span>
          )}
          {(localSession.apprenants_presents || []).length > 0 && (
            <span className='bg-green-500/20 text-green-300 text-xs px-2 py-1 rounded'>
              {localSession.apprenants_presents.length} apprenant(s)
            </span>
          )}
        </div>

        {canRemove && (
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              onRemove(index);
            }}
            className='text-red-400 hover:text-red-300 p-1'>
            <Trash2 className='w-5 h-5' />
          </button>
        )}
      </div>

      {isExpanded && (
        <div className='p-4 bg-slate-800/50 space-y-6'>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div>
              <label className='block text-sm font-medium text-gray-300 mb-1'>
                Date de la session *
              </label>
              <input
                type='date'
                value={localSession.date_session || ""}
                onChange={(e) => handleChange("date_session", e.target.value)}
                className='w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500'
              />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-300 mb-1'>
                Heure de début *
              </label>
              <div className='relative'>
                <Clock className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                <input
                  type='time'
                  value={localSession.heure_debut || "08:00"}
                  onChange={(e) => handleChange("heure_debut", e.target.value)}
                  className='w-full bg-slate-700 border border-slate-600 rounded-lg p-3 pl-10 text-white focus:outline-none focus:border-blue-500'
                />
              </div>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-300 mb-1'>
                Heure de fin *
              </label>
              <div className='relative'>
                <Clock className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                <input
                  type='time'
                  value={localSession.heure_fin || "17:00"}
                  onChange={(e) => handleChange("heure_fin", e.target.value)}
                  className='w-full bg-slate-700 border border-slate-600 rounded-lg p-3 pl-10 text-white focus:outline-none focus:border-blue-500'
                />
              </div>
            </div>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-300 mb-1'>
              Formateur responsable de cette session
            </label>
            <select
              value={localSession.formateur_id || ""}
              onChange={(e) => {
                const trainer = trainers.find(
                  (t) => t.id.toString() === e.target.value
                );
                handleChange("formateur_id", e.target.value);
                handleChange("formateur_name", trainer?.name || "");
              }}
              className='w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 appearance-none'>
              <option value=''>Utiliser le formateur principal</option>
              {trainers.map((trainer) => (
                <option key={trainer.id} value={trainer.id}>
                  {trainer.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-300 mb-1'>
              Travaux réalisés lors de cette session
            </label>
            <textarea
              value={localSession.travaux_realises || ""}
              onChange={(e) => handleChange("travaux_realises", e.target.value)}
              placeholder='Décrivez les travaux réalisés pendant cette session...'
              rows={3}
              className='w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-300 mb-1'>
              Observations / Remarques
            </label>
            <textarea
              value={localSession.observations || ""}
              onChange={(e) => handleChange("observations", e.target.value)}
              placeholder='Observations particulières, difficultés rencontrées, points à améliorer...'
              rows={2}
              className='w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500'
            />
          </div>

          <div>
            <div className='flex items-center justify-between mb-4'>
              <div className='flex items-center gap-2'>
                <Users className='w-5 h-5 text-blue-400' />
                <h4 className='text-lg font-medium text-blue-400'>
                  Apprenants présents à cette session
                </h4>
              </div>
              <button
                type='button'
                onClick={addApprenant}
                className='flex items-center text-sm text-blue-400 hover:text-blue-300 bg-slate-700/50 px-3 py-1.5 rounded-lg'>
                <UserPlus className='w-4 h-4 mr-1' />
                Ajouter
              </button>
            </div>

            {(localSession.apprenants_presents || []).map(
              (apprenant, appIndex) => (
                <div
                  key={apprenant.id || appIndex}
                  className='mb-4 p-4 bg-slate-700/30 rounded-lg'>
                  <div className='flex justify-between items-center mb-3'>
                    <span className='text-sm font-medium text-gray-300'>
                      Apprenant #{appIndex + 1}
                    </span>
                    {(localSession.apprenants_presents || []).length > 1 && (
                      <button
                        type='button'
                        onClick={() => removeApprenant(appIndex)}
                        className='text-red-400 hover:text-red-300'>
                        <X className='w-4 h-4' />
                      </button>
                    )}
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
                    <div>
                      <label className='block text-xs font-medium text-gray-400 mb-1'>
                        Groupe
                      </label>
                      <select
                        value={apprenant.groupe || ""}
                        onChange={(e) =>
                          handleApprenantChange(
                            appIndex,
                            "groupe",
                            e.target.value
                          )
                        }
                        className='w-full bg-slate-700 border border-slate-600 rounded-lg p-2 text-white text-sm focus:outline-none focus:border-blue-500'>
                        <option value=''>Sélectionner un groupe</option>
                        {groupes.map((groupe) => (
                          <option key={groupe.id} value={groupe.id}>
                            {groupe.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className='block text-xs font-medium text-gray-400 mb-1'>
                        Nom et prénom
                      </label>
                      <ApprenantSelector
                        groupe={apprenant.groupe}
                        selectedName={apprenant.name}
                        onSelect={(name, id) =>
                          handleApprenantChange(appIndex, "name", name, id)
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <label className='block text-xs font-medium text-gray-400 mb-2'>
                      Compétences travaillées par cet apprenant
                    </label>
                    <CompetenceSelector
                      typeFormation={
                        apprenant.groupe ?
                          groupes.find((g) => g.id === apprenant.groupe)
                            ?.typeFormation
                        : null
                      }
                      selectedCompetences={apprenant.competences || []}
                      onCompetenceChange={(competences) =>
                        handleApprenantCompetencesChange(appIndex, competences)
                      }
                    />


                    {/* ✅ NOUVEAU V8 : Liste des compétences sélectionnées avec niveau */}
                    {(() => {
                      console.log("🔍 DEBUG V8 - Apprenant:", apprenant);
                      console.log(
                        "🔍 DEBUG V8 - Compétences:",
                        apprenant.competences
                      );
                      console.log(
                        "🔍 DEBUG V8 - Nombre:",
                        apprenant.competences?.length
                      );
                      return null;
                    })()}
                    {apprenant.competences &&
                      apprenant.competences.length > 0 && (
                        <div className='mt-4 p-4 bg-blue-900/20 rounded-lg border border-blue-700'>
                          <h4 className='text-sm font-semibold text-blue-300 mb-3 flex items-center gap-2'>
                            <BarChart3 className='w-4 h-4' />
                            Compétences sélectionnées (
                            {apprenant.competences.length})
                          </h4>
                          <div className='space-y-2'>
                            {apprenant.competences.map((comp, compIndex) => (
                              <div
                                key={`${comp.tache_id || comp.activite_id}-${compIndex}`}
                                className='flex items-center justify-between bg-slate-800 p-3 rounded-lg'>
                                <div className='flex-1'>
                                  <p className='text-sm font-medium text-white'>
                                    {comp.tache_code || comp.activite_code} -{" "}
                                    {comp.tache_titre || comp.activite_titre}
                                  </p>
                                  <p className='text-xs text-gray-400'>
                                    {comp.pole_code} → {comp.bloc_code}
                                  </p>
                                </div>
                                <div className='ml-4'>
                                  <select
                                    value={comp.niveau || "découverte"}
                                    onChange={(e) => {
                                      const updatedCompetences = [
                                        ...apprenant.competences,
                                      ];
                                      updatedCompetences[compIndex].niveau =
                                        e.target.value;
                                      handleApprenantCompetencesChange(
                                        appIndex,
                                        updatedCompetences
                                      );
                                    }}
                                    className='px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:border-blue-500 focus:outline-none'>
                                    <option value='découverte'>
                                      🔵 Découverte
                                    </option>
                                    <option value='application'>
                                      🟢 Application
                                    </option>
                                    <option value='maîtrise'>
                                      🟡 Maîtrise
                                    </option>
                                    <option value='expert'>🔴 Expert</option>
                                  </select>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ✅ Sélecteur de mode d'intervention
const InterventionModeSelector = ({ mode, onModeChange }) => {
  return (
    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
      <button
        type='button'
        onClick={() => onModeChange("simple")}
        className={`p-6 rounded-xl border-2 transition-all ${
          mode === "simple" ?
            "border-blue-500 bg-blue-500/20"
          : "border-slate-600 bg-slate-700/50 hover:border-slate-500"
        }`}>
        <div className='flex items-center gap-4'>
          <div
            className={`p-3 rounded-full ${mode === "simple" ? "bg-blue-500" : "bg-slate-600"}`}>
            <Calendar className='w-6 h-6 text-white' />
          </div>
          <div className='text-left'>
            <h3 className='text-lg font-semibold text-white'>
              Intervention simple
            </h3>
            <p className='text-sm text-gray-400'>
              Réalisée en une seule journée ou session
            </p>
          </div>
        </div>
        {mode === "simple" && (
          <div className='mt-4 flex items-center text-blue-400 text-sm'>
            <CheckCircle className='w-4 h-4 mr-2' />
            Mode sélectionné
          </div>
        )}
      </button>

      <button
        type='button'
        onClick={() => onModeChange("multi")}
        className={`p-6 rounded-xl border-2 transition-all ${
          mode === "multi" ?
            "border-purple-500 bg-purple-500/20"
          : "border-slate-600 bg-slate-700/50 hover:border-slate-500"
        }`}>
        <div className='flex items-center gap-4'>
          <div
            className={`p-3 rounded-full ${mode === "multi" ? "bg-purple-500" : "bg-slate-600"}`}>
            <Layers className='w-6 h-6 text-white' />
          </div>
          <div className='text-left'>
            <h3 className='text-lg font-semibold text-white'>Multi-sessions</h3>
            <p className='text-sm text-gray-400'>
              Sur plusieurs jours ou semaines
            </p>
          </div>
        </div>
        {mode === "multi" && (
          <div className='mt-4 flex items-center text-purple-400 text-sm'>
            <CheckCircle className='w-4 h-4 mr-2' />
            Mode sélectionné
          </div>
        )}
      </button>
    </div>
  );
};

// ✅ Barre de progression pour l'avancement
const ProgressBar = ({ value, onChange, disabled = false }) => {
  const getColorClass = (val) => {
    if (val < 25) return "bg-red-500";
    if (val < 50) return "bg-orange-500";
    if (val < 75) return "bg-yellow-500";
    if (val < 100) return "bg-blue-500";
    return "bg-green-500";
  };

  return (
    <div className='space-y-2'>
      <div className='flex justify-between items-center'>
        <span className='text-sm text-gray-400'>
          Avancement de l'intervention
        </span>
        <span
          className={`text-lg font-bold ${value === 100 ? "text-green-400" : "text-blue-400"}`}>
          {value}%
        </span>
      </div>
      <div className='relative'>
        <div className='h-3 bg-slate-700 rounded-full overflow-hidden'>
          <div
            className={`h-full transition-all duration-300 ${getColorClass(value)}`}
            style={{ width: `${value}%` }}
          />
        </div>
        {!disabled && (
          <input
            type='range'
            min='0'
            max='100'
            step='5'
            value={value}
            onChange={(e) => onChange(parseInt(e.target.value))}
            className='absolute inset-0 w-full h-full opacity-0 cursor-pointer'
          />
        )}
      </div>
      <div className='flex justify-between text-xs text-gray-500'>
        <span>Début</span>
        <span>25%</span>
        <span>50%</span>
        <span>75%</span>
        <span>Terminé</span>
      </div>
    </div>
  );
};

// ✅ Sélecteur de statut
const StatusSelector = ({ status, onStatusChange }) => {
  const statuses = [
    {
      value: "planifiee",
      label: "Planifiée",
      color: "bg-gray-500",
      icon: Calendar,
    },
    {
      value: "en_cours",
      label: "En cours",
      color: "bg-blue-500",
      icon: Wrench,
    },
    {
      value: "terminee",
      label: "Terminée",
      color: "bg-green-500",
      icon: CheckCircle,
    },
    { value: "annulee", label: "Annulée", color: "bg-red-500", icon: X },
  ];

  return (
    <div className='flex flex-wrap gap-2'>
      {statuses.map((s) => {
        const Icon = s.icon;
        return (
          <button
            key={s.value}
            type='button'
            onClick={() => onStatusChange(s.value)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              status === s.value ?
                `${s.color} text-white`
              : "bg-slate-700 text-gray-400 hover:bg-slate-600"
            }`}>
            <Icon className='w-4 h-4' />
            {s.label}
          </button>
        );
      })}
    </div>
  );
};

// ✅ COMPOSANT PRINCIPAL
const InterventionForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const controls = useAnimation();
  const logoControls = useAnimation();
  const formRef = useRef(null);
  const pdfRef = useRef(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [showClientsList, setShowClientsList] = useState(false);
  const [allClients, setAllClients] = useState([]);

  const [searchPlate, setSearchPlate] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  const apprenticeSignatureRefs = useRef([]);
  const trainerSignatureRef = useRef(null);

  const [photos, setPhotos] = useState({
    frontBefore: null,
    rightBefore: null,
    leftBefore: null,
    backBefore: null,
    frontAfter: null,
    rightAfter: null,
    leftAfter: null,
    backAfter: null,
  });

  const [interventionMode, setInterventionMode] = useState("simple");
  const [sessions, setSessions] = useState([]);
  const [expandedSessions, setExpandedSessions] = useState({});

  const groupes = [
    { id: "CAP 1 A MVA", name: "CAP 1 A MVA", typeFormation: "CAP_MVA" },
    { id: "CAP 1 B MVA", name: "CAP 1 B MVA", typeFormation: "CAP_MVA" },
    { id: "CAP 2 A MVA", name: "CAP 2 A MVA", typeFormation: "CAP_MVA" },
    { id: "CAP 2 B MVA", name: "CAP 2 B MVA", typeFormation: "CAP_MVA" },
    { id: "CAP 1 AN PA", name: "CAP 1 AN PA", typeFormation: "CAP 1 AN PA" },
    { id: "CAP 1 CA", name: "CAP 1 CA", typeFormation: "CAP_CARROSSERIE" },
    { id: "CAP 2 CA", name: "CAP 2 CA", typeFormation: "CAP_CARROSSERIE" },
    { id: "CQP_TAVA", name: "CQP TAVA", typeFormation: "CQP_MECANIQUE" },
    { id: "BCP 1/3 MVA", name: "BCP 1/3 MVA", typeFormation: "BAC_PRO_MVA" },
    {
      id: "BCP 2/3 A MVA",
      name: "BCP 2/3 A MVA",
      typeFormation: "BAC_PRO_MVA",
    },
    {
      id: "BCP 2/3 B MVA",
      name: "BCP 2/3 B MVA",
      typeFormation: "BAC_PRO_MVA",
    },
    {
      id: "BCP 3/3 A MVA",
      name: "BCP 3/3 A MVA",
      typeFormation: "BAC_PRO_MVA",
    },
    {
      id: "BCP 3/3 B MVA",
      name: "BCP 3/3 B MVA",
      typeFormation: "BAC_PRO_MVA",
    },
  ];

  const checklistItems = [
    { id: 1, title: "État des pneumatiques", checked: false },
    { id: 2, title: "Niveau des liquides", checked: false },
    { id: 3, title: "Fonctionnement des feux", checked: false },
    { id: 4, title: "Freinage", checked: false },
    { id: 5, title: "Essai routier", checked: false },
    { id: 6, title: "Propreté intérieure", checked: false },
    { id: 7, title: "Propreté extérieure", checked: false },
    { id: 8, title: "Contrôle de la géométrie", checked: false },
    { id: 9, title: "État de la carrosserie", checked: false },
    { id: 10, title: "Mise à jour du carnet d'entretien", checked: false },
  ];

  const [trainers, setTrainers] = useState([]);
  const [trainersLoaded, setTrainersLoaded] = useState(false);

  const [formData, setFormData] = useState({
    immatriculation: "",
    clientName: "",
    clientId: null,
    interventionType: "mechanical",
    workDescription: "",
    futureMaintenance: "",
    trainerId: "",
    completionDate: new Date().toISOString().split("T")[0],
    checklist: [...checklistItems],
    apprentices: [
      {
        id: 1,
        name: "",
        groupe: "",
        apprenantId: null,
        signature: null,
        competences: [],
      },
    ],
    photos: { ...photos },
    date_debut: "",
    date_debut_heure: "08:00",
    date_fin: "",
    date_fin_heure: "17:00",
    status: "planifiee",
    avancement: 0,
    is_multi_session: false,
    date_debut_prevue: "",
    date_fin_prevue: "",
    trainerSignatureData: null,
  });

  const [signatures, setSignatures] = useState({
    trainer: null,
    apprentices: [],
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingInterventionId, setEditingInterventionId] = useState(null);

  // ✅ V8.3 : Gestion réception
  const [showReceptionForm, setShowReceptionForm] = useState(false);
  const [receptionData, setReceptionData] = useState(null);
  const [receptionCompleted, setReceptionCompleted] = useState(false);
  const [showReceptionSearch, setShowReceptionSearch] = useState(false);

  // ✨ Calculer la durée entre deux dates
  const calculateDuration = (dateDebut, heureDebut, dateFin, heureFin) => {
    // ✅ Vérifier que TOUS les champs existent
    if (!dateDebut || !dateFin) return "À compléter";

    try {
      const debut = new Date(`${dateDebut}T${heureDebut || "00:00"}:00`);
      const fin = new Date(`${dateFin}T${heureFin || "00:00"}:00`);

      // ✅ Vérifier que les dates sont valides
      if (isNaN(debut.getTime()) || isNaN(fin.getTime())) {
        return "Date invalide";
      }

      const diffMs = fin - debut;

      if (diffMs < 0) return "⚠️ La date de fin est avant la date de début";

      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);
      const remainingHours = diffHours % 24;
      const remainingMinutes = diffMinutes % 60;

      // ✅ Vérifier que les calculs sont valides
      if (isNaN(remainingMinutes)) return "Erreur de calcul";

      if (diffDays > 0) {
        return `${diffDays} jour${diffDays > 1 ? "s" : ""} ${remainingHours}h${remainingMinutes > 0 ? ` ${remainingMinutes}min` : ""}`;
      }
      if (diffHours > 0) {
        return `${diffHours}h${remainingMinutes > 0 ? ` ${remainingMinutes}min` : ""}`;
      }
      return `${remainingMinutes} minute${remainingMinutes > 1 ? "s" : ""}`;
    } catch (error) {
      console.error("Erreur calcul durée:", error);
      return "Erreur de calcul";
    }
  };

  const calculateTotalSessionsDuration = () => {
    let totalMinutes = 0;

    sessions.forEach((session) => {
      if (session.heure_debut && session.heure_fin) {
        const [startH, startM] = session.heure_debut.split(":").map(Number);
        const [endH, endM] = session.heure_fin.split(":").map(Number);

        let diffMinutes = endH * 60 + endM - (startH * 60 + startM);
        if (diffMinutes < 0) diffMinutes += 24 * 60;

        totalMinutes += diffMinutes;
      }
    });

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return `${hours}h${minutes > 0 ? ` ${minutes}min` : ""}`;
    }
    return minutes > 0 ? `${minutes}min` : "0min";
  };

  const addSession = () => {
    const newSession = {
      id: `temp-${Date.now()}`,
      numero_session: sessions.length + 1,
      date_session: "",
      heure_debut: "08:00",
      heure_fin: "17:00",
      travaux_realises: "",
      observations: "",
      formateur_id: "",
      formateur_name: "",
      apprenants_presents: [
        {
          id: 1,
          name: "",
          groupe: "",
          apprenantId: null,
          signature: null,
          competences: [],
        },
      ],
      competences_session: [],
      status: "planifiee",
    };

    setSessions([...sessions, newSession]);
    setExpandedSessions((prev) => ({ ...prev, [newSession.id]: true }));
  };

  const updateSession = (index, updatedSession) => {
    const newSessions = [...sessions];
    newSessions[index] = updatedSession;
    setSessions(newSessions);
  };

  const removeSession = (index) => {
    if (sessions.length > 1) {
      const newSessions = sessions.filter((_, i) => i !== index);
      newSessions.forEach((s, i) => (s.numero_session = i + 1));
      setSessions(newSessions);
    }
  };

  const toggleSessionExpand = (sessionId) => {
    setExpandedSessions((prev) => ({ ...prev, [sessionId]: !prev[sessionId] }));
  };

  const handleModeChange = (mode) => {
    setInterventionMode(mode);
    setFormData((prev) => ({
      ...prev,
      is_multi_session: mode === "multi",
    }));

    if (mode === "multi" && sessions.length === 0) {
      const firstSession = {
        id: `temp-${Date.now()}`,
        numero_session: 1,
        date_session: formData.date_debut || "",
        heure_debut: formData.date_debut_heure || "08:00",
        heure_fin: formData.date_fin_heure || "17:00",
        travaux_realises: "",
        observations: "",
        formateur_id: formData.trainerId || "",
        formateur_name:
          trainers.find((t) => t.id.toString() === formData.trainerId)?.name ||
          "",
        apprenants_presents:
          formData.apprentices.length > 0 ?
            [...formData.apprentices]
          : [
              {
                id: 1,
                name: "",
                groupe: "",
                apprenantId: null,
                signature: null,
                competences: [],
              },
            ],
        competences_session: [],
        status: "planifiee",
      };

      setSessions([firstSession]);
      setExpandedSessions({ [firstSession.id]: true });
    }
  };

  useEffect(() => {
    const loadAllClients = async () => {
      try {
        const { data, error } = await supabase
          .from("clients")
          .select("id, immatriculation, first_name, last_name")
          .order("last_name", { ascending: true });

        if (error) {
          console.error("Erreur lors du chargement des clients:", error);
          return;
        }

        if (data) {
          setAllClients(
            data.map((client) => ({
              ...client,
              first_name: cleanText(client.first_name),
              last_name: cleanText(client.last_name),
              fullName: `${cleanText(client.last_name)} ${cleanText(client.first_name)}`,
            }))
          );
        }
      } catch (error) {
        console.error("Erreur lors du chargement des clients:", error);
      }
    };

    loadAllClients();
  }, []);

  // ✅ useEffect modifié pour appeler la fonction async
  useEffect(() => {
    // ✅ CRITIQUE: Attendre que trainers soit chargé AVANT de charger l'intervention
    if (!trainersLoaded) {
      console.log("⏳ En attente du chargement des formateurs...");
      return;
    }

    const interventionToEdit = location.state?.intervention;

    if (interventionToEdit) {
      console.log("📝 Mode ÉDITION détecté:", interventionToEdit);
      console.log("✅ Formateurs chargés, peut charger intervention");
      setIsEditing(true);
      setEditingInterventionId(interventionToEdit.id);
      loadInterventionData(interventionToEdit);
    } else {
      console.log("📝 Mode CRÉATION (nouvelle intervention)");
    }
  }, [location, trainersLoaded]);

  useEffect(() => {
    const loadFormateurs = async () => {
      try {
        console.log("🔄 Chargement des formateurs...");
        const { data, error } = await supabase
          .from("formateurs")
          .select("id, nom, prenom")
          .order("nom");

        if (error) {
          console.error("❌ Erreur lors du chargement des formateurs:", error);
          setTrainersLoaded(true); // Même en cas d'erreur, on continue
          return;
        }

        if (data && data.length > 0) {
          const formattedTrainers = data.map((f) => ({
            id: f.id,
            name: `${cleanText(f.prenom)} ${cleanText(f.nom)}`,
          }));

          setTrainers(formattedTrainers);
          console.log("✅ Formateurs chargés:", formattedTrainers.length);
        } else {
          console.log("⚠️ Aucun formateur trouvé");
        }
        setTrainersLoaded(true);
      } catch (error) {
        console.error("❌ Erreur lors du chargement des formateurs:", error);
        setTrainersLoaded(true);
      }
    };

    loadFormateurs();
  }, []);

  useEffect(() => {
    const logoAnimation = async () => {
      await logoControls.start({
        scale: [0.8, 1.2, 1],
        rotate: [0, 15, -15, 0],
        opacity: [0, 1],
        transition: { duration: 2 },
      });

      logoControls.start({
        y: [0, -15, 0],
        scale: [1, 1.1, 1],
        transition: {
          repeat: Infinity,
          duration: 4,
          ease: "easeInOut",
        },
      });
    };

    logoAnimation();
  }, [logoControls]);

  useEffect(() => {
    apprenticeSignatureRefs.current = formData.apprentices.map(
      (_, i) => apprenticeSignatureRefs.current[i] || { current: null }
    );
  }, [formData.apprentices.length]);

  useEffect(() => {
    if (formData.interventionType === "mechanical") {
      setPhotos((prev) => ({
        ...prev,
        frontAfter: null,
        rightAfter: null,
        leftAfter: null,
        backAfter: null,
      }));
    }
  }, [formData.interventionType]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const selectedDate = urlParams.get("date");

    if (location?.state?.selectedDate) {
      setFormData((prev) => ({
        ...prev,
        date_debut: location.state.selectedDate,
        date_fin: location.state.selectedDate,
        date_debut_prevue: location.state.selectedDate,
        date_fin_prevue: location.state.selectedDate,
      }));
    } else if (selectedDate) {
      setFormData((prev) => ({
        ...prev,
        date_debut: selectedDate,
        date_fin: selectedDate,
        date_debut_prevue: selectedDate,
        date_fin_prevue: selectedDate,
      }));
    }
  }, [location]);

  // ✅ FONCTION loadInterventionData avec URLs signées
  const loadInterventionData = async (intervention) => {
    console.log("📥 Chargement intervention pour édition:", intervention);

    // ✅ V8.3 : Charger réception si elle existe
    if (intervention.reception_id) {
      console.log("📦 [V8.3] Chargement réception:", intervention.reception_id);
      
      try {
        const { data: reception, error } = await supabase
          .from('receptions')
          .select('*')
          .eq('id', intervention.reception_id)
          .single();
        
        if (!error && reception) {
          console.log("✅ [V8.3] Réception chargée:", reception);
          
          // Charger URLs signées des photos de réception
          if (reception.photos && typeof reception.photos === 'object') {
            const photoFiles = {};
            for (const [key, fileName] of Object.entries(reception.photos)) {
              if (fileName) {
                photoFiles[key] = fileName;
              }
            }
            
            if (Object.keys(photoFiles).length > 0) {
              const photosWithUrls = await getSignedUrls('vehicle-photos', photoFiles);
              reception.photos = photosWithUrls;
              console.log("📷 [V8.3] URLs signées photos réception:", Object.keys(photosWithUrls));
            }
          }
          
          setReceptionData(reception);
          setReceptionCompleted(true);
          setShowReceptionForm(false);
          
          console.log("✅ [V8.3] État réception initialisé");
        } else {
          console.log("⚠️ [V8.3] Réception non trouvée (peut-être ancienne intervention)");
        }
      } catch (err) {
        console.error("❌ [V8.3] Erreur chargement réception:", err);
      }
    } else {
      console.log("ℹ️ [V8.3] Pas de reception_id (ancienne intervention)");
    }

    // ✅ CORRECTION: Toujours chercher le nom par trainer_id dans la liste trainers
    // car trainer_name peut être "Non assigné" même si le formateur existe
    const existingTrainerName =
      trainers.find(
        (t) => t.id.toString() === intervention.trainer_id?.toString()
      )?.name ||
      ((
        intervention.trainer_name &&
        intervention.trainer_name !== "Non assigné" &&
        intervention.trainer_name !== "Non spécifié"
      ) ?
        intervention.trainer_name
      : "");

    console.log("👨‍🏫 Formateur:", {
      trainer_id: intervention.trainer_id,
      trainer_name_DB: intervention.trainer_name,
      trainer_name_trouvé: existingTrainerName,
      trainers_disponibles: trainers.length,
      trainers_liste: trainers
        .slice(0, 5)
        .map((t) => ({ id: t.id, name: t.name })),
    });

    // ✅ Préparer les photos avec URLs signées
    let photosWithUrls = {};
    if (intervention.photos) {
      console.log(
        "🔍 Photos brutes:",
        typeof intervention.photos,
        intervention.photos
      );

      // CRITIQUE: Si c'est une STRING JSON, la parser !
      let parsedPhotos = intervention.photos;
      if (typeof intervention.photos === "string") {
        try {
          parsedPhotos = JSON.parse(intervention.photos);
          console.log("✅ Photos parsées:", parsedPhotos);
        } catch (e) {
          console.error("❌ Erreur parsing photos:", e);
          parsedPhotos = {};
        }
      }

      if (parsedPhotos && Object.keys(parsedPhotos).length > 0) {
        console.log("📷 Photos existantes:", parsedPhotos);

        const photoFiles = {};
        for (const [key, value] of Object.entries(parsedPhotos)) {
          if (value) {
            let fileName = null;

            if (typeof value === "string") {
              if (value.includes("vehicle-photos")) {
                const parts = value.split("/");
                fileName = parts[parts.length - 1].split("?")[0];
              } else if (
                !value.startsWith("http") &&
                !value.startsWith("data:")
              ) {
                fileName = value;
              }
            } else if (typeof value === "object" && value.publicUrl) {
              const parts = value.publicUrl.split("/");
              fileName = parts[parts.length - 1].split("?")[0];
            }

            if (fileName) {
              photoFiles[key] = fileName;
            }
          }
        }

        console.log("🔐 Création des URLs signées pour les photos...");
        photosWithUrls = await getSignedUrls("vehicle-photos", photoFiles);
        console.log("📷 Photos avec URLs signées:", photosWithUrls);
      }
    }

    // ✅ Préparer la signature formateur avec URL signée
    let trainerSigUrl = null;
    if (intervention.trainer_signature) {
      console.log(
        "🔍 trainer_signature brut:",
        typeof intervention.trainer_signature,
        intervention.trainer_signature
      );

      // CRITIQUE: Si c'est une STRING JSON, la parser !
      let parsedSignature = intervention.trainer_signature;
      if (
        typeof intervention.trainer_signature === "string" &&
        intervention.trainer_signature.startsWith("{")
      ) {
        try {
          parsedSignature = JSON.parse(intervention.trainer_signature);
          console.log("✅ trainer_signature parsé:", parsedSignature);
        } catch (e) {
          console.error("❌ Erreur parsing trainer_signature:", e);
        }
      }

      let sigFileName = null;

      if (typeof parsedSignature === "object" && parsedSignature !== null) {
        sigFileName = parsedSignature.fileName;
        console.log("📁 fileName extrait de l'objet:", sigFileName);
      } else if (typeof parsedSignature === "string") {
        if (parsedSignature.startsWith("data:")) {
          trainerSigUrl = parsedSignature;
        } else if (parsedSignature.includes("signatures")) {
          const parts = parsedSignature.split("/");
          sigFileName = parts[parts.length - 1].split("?")[0];
        } else if (!parsedSignature.startsWith("http")) {
          sigFileName = parsedSignature;
        }
      }

      if (sigFileName && !trainerSigUrl) {
        console.log(
          "🔐 Création URL signée pour signature formateur:",
          sigFileName
        );
        trainerSigUrl = await getSignedUrl("signatures", sigFileName);
      }

      console.log(
        "✅ Signature formateur chargée:",
        trainerSigUrl ? "OK" : "Échec"
      );
    }

    setPhotos(photosWithUrls);

    // ✅ Charger les signatures des apprentis avec URLs signées
    const apprenticeSignaturesUrls = [];

    if (intervention.apprentices && Array.isArray(intervention.apprentices)) {
      console.log(
        "👥 Chargement signatures apprentis:",
        intervention.apprentices.length,
        "apprentis"
      );

      for (let index = 0; index < intervention.apprentices.length; index++) {
        const apprentice = intervention.apprentices[index];
        let appSigUrl = null;

        console.log(
          `👤 Apprenti #${index + 1}:`,
          apprentice.name,
          "- Signature:",
          apprentice.signature
        );

        if (apprentice.signature) {
          let sigFileName = null;

          // Cas 1: Objet avec fileName
          if (
            typeof apprentice.signature === "object" &&
            apprentice.signature !== null
          ) {
            sigFileName = apprentice.signature.fileName;
            console.log(`  📁 Type objet, fileName: ${sigFileName}`);
          }
          // Cas 2: String
          else if (typeof apprentice.signature === "string") {
            // Déjà un base64
            if (apprentice.signature.startsWith("data:")) {
              appSigUrl = apprentice.signature;
              console.log(`  📝 Type base64 direct`);
            }
            // URL Supabase (signée ou publique)
            else if (
              apprentice.signature.includes("supabase") ||
              apprentice.signature.includes("signatures")
            ) {
              const parts = apprentice.signature.split("/");
              sigFileName = parts[parts.length - 1].split("?")[0];
              console.log(`  🔗 Type URL, fileName extrait: ${sigFileName}`);
            }
            // Juste un nom de fichier
            else if (!apprentice.signature.startsWith("http")) {
              sigFileName = apprentice.signature;
              console.log(`  📄 Type nom de fichier: ${sigFileName}`);
            }
          }

          // Créer l'URL signée si on a un nom de fichier
          if (sigFileName && !appSigUrl) {
            console.log(`  🔐 Création URL signée pour: ${sigFileName}`);
            appSigUrl = await getSignedUrl("signatures", sigFileName);

            if (appSigUrl) {
              console.log(`  ✅ URL signée créée pour apprenti #${index + 1}`);
            } else {
              console.log(
                `  ❌ Échec création URL signée pour apprenti #${index + 1}`
              );
            }
          }
        }

        apprenticeSignaturesUrls[index] = appSigUrl;
      }
    }

    console.log(
      "📋 Résumé signatures apprentis:",
      apprenticeSignaturesUrls.map((s, i) => ({
        index: i + 1,
        hasSignature: !!s,
        type:
          s ?
            s.startsWith("data:") ?
              "base64"
            : "signedUrl"
          : "none",
      }))
    );

    // ✅ Mettre à jour les apprentis avec leurs signatures
    const apprenticesWithSignatures = (intervention.apprentices || []).map(
      (apprentice, index) => ({
        ...apprentice,
        id: apprentice.id || index + 1,
        name: apprentice.name || "",
        groupe: apprentice.groupe || "",
        apprenantId: apprentice.apprenant_id || apprentice.apprenantId || null,
        competences: apprentice.competences || [],
        signature: apprentice.signature,
      })
    );

    setFormData({
      clientId: intervention.client_id || "",
      immatriculation: intervention.immatriculation || "",
      clientName: intervention.client_name || "",
      interventionType: intervention.intervention_type || "mechanical",
      workDescription: intervention.work_description || "",
      futureMaintenance: intervention.future_maintenance || "",
      trainerId: intervention.trainer_id?.toString() || "",
      trainerName: existingTrainerName,
      completionDate:
        intervention.completion_date || new Date().toISOString().split("T")[0],
      date_debut:
        intervention.date_debut ? intervention.date_debut.split("T")[0] : "",
      date_fin:
        intervention.date_fin ? intervention.date_fin.split("T")[0] : "",
      date_debut_heure: intervention.date_debut_heure || "08:00",
      date_fin_heure: intervention.date_fin_heure || "17:00",
      date_debut_prevue: intervention.date_debut_prevue || "",
      date_fin_prevue: intervention.date_fin_prevue || "",
      status: intervention.status || "planifiee",
      avancement: intervention.avancement || 0,
      is_multi_session: intervention.is_multi_session || false,
      checklist: checklistItems.map((item) => ({
        ...item,
        checked: intervention.checklist_done?.includes(item.id) || false,
      })),
      apprentices: apprenticesWithSignatures,
      photos: photosWithUrls,
      trainerSignatureData: trainerSigUrl,
    });

    if (intervention.is_multi_session) {
      setInterventionMode("multi");
    } else {
      setInterventionMode("simple");
    }

    // ✅ Mettre à jour l'état des signatures avec les URLs signées
    setSignatures({
      trainer: trainerSigUrl,
      apprentices: apprenticeSignaturesUrls,
    });

    console.log(
      "✅ FormData mis à jour avec trainerId:",
      intervention.trainer_id?.toString() || ""
    );
    console.log("✅ État signatures mis à jour:", {
      trainer: trainerSigUrl ? "OK" : "null",
      apprentices: apprenticeSignaturesUrls.map(
        (s, i) => `#${i + 1}: ${s ? "OK" : "null"}`
      ),
    });
  };

  const searchClientByPlate = async (e) => {
    e.preventDefault();

    if (!searchPlate.trim()) {
      setSearchError("Veuillez saisir une immatriculation");
      return;
    }

    setIsSearching(true);
    setSearchError("");

    try {
      const { data, error } = await supabase
        .from("clients")
        .select("id, immatriculation, first_name, last_name")
        .eq("immatriculation", searchPlate.trim())
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          setSearchError("Aucun client trouvé avec cette immatriculation");
        } else {
          setSearchError("Une erreur est survenue lors de la recherche");
          console.error("Erreur Supabase:", error);
        }
        return;
      }

      if (data) {
        setFormData((prev) => ({
          ...prev,
          immatriculation: data.immatriculation,
          clientName: `${cleanText(data.last_name)} ${cleanText(data.first_name)}`,
          clientId: data.id,
        }));
      }
    } catch (error) {
      console.error("Erreur lors de la recherche:", error);
      setSearchError("Une erreur est survenue lors de la recherche");
    } finally {
      setIsSearching(false);
    }
  };

  const loadClientById = (clientId) => {
    const client = allClients.find((c) => c.id === clientId);
    if (client) {
      setFormData((prev) => ({
        ...prev,
        immatriculation: client.immatriculation,
        clientName: client.fullName,
        clientId: client.id,
      }));
      setShowClientsList(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleChecklistChange = (id) => {
    setFormData((prev) => ({
      ...prev,
      checklist: prev.checklist.map((item) =>
        item.id === id ? { ...item, checked: !item.checked } : item
      ),
    }));
  };

  const handleApprenticeChange = (index, field, value, apprenantId = null) => {
    const updatedApprentices = [...formData.apprentices];
    updatedApprentices[index] = {
      ...updatedApprentices[index],
      [field]: value,
    };

    if (apprenantId) {
      updatedApprentices[index].apprenantId = apprenantId;
    }

    if (field === "groupe") {
      updatedApprentices[index].competences = [];
    }

    setFormData((prev) => ({ ...prev, apprentices: updatedApprentices }));

    if (errors[`apprentice_${index}_${field}`]) {
      setErrors((prev) => ({
        ...prev,
        [`apprentice_${index}_${field}`]: null,
      }));
    }
  };

  const handleApprenticeCompetencesChange = (index, competences) => {
    const updatedApprentices = [...formData.apprentices];
    updatedApprentices[index] = {
      ...updatedApprentices[index],
      competences: competences,
    };
    setFormData((prev) => ({ ...prev, apprentices: updatedApprentices }));
  };

  // ✅ V8.3 : handlePhotoUpload supprimé - photos gérées par ReceptionForm

  const addApprentice = () => {
    setFormData((prev) => ({
      ...prev,
      apprentices: [
        ...prev.apprentices,
        {
          id: prev.apprentices.length + 1,
          name: "",
          groupe: "",
          apprenantId: null,
          signature: null,
          competences: [],
        },
      ],
    }));
  };

  const removeApprentice = (index) => {
    if (formData.apprentices.length > 1) {
      const updatedApprentices = formData.apprentices.filter(
        (_, i) => i !== index
      );
      setFormData((prev) => ({ ...prev, apprentices: updatedApprentices }));
    }
  };

  const clearApprenticeSignature = (index) => {
    const sigRef = apprenticeSignatureRefs.current[index];
    if (sigRef && sigRef.current) {
      sigRef.current.clear();
    }
    // Aussi effacer de l'état signatures
    setSignatures((prev) => {
      const newApprentices = [...prev.apprentices];
      newApprentices[index] = null;
      return { ...prev, apprentices: newApprentices };
    });
  };

  const clearTrainerSignature = () => {
    if (trainerSignatureRef.current) {
      trainerSignatureRef.current.clear();
    }
    // Aussi effacer de l'état
    setSignatures((prev) => ({ ...prev, trainer: null }));
    setFormData((prev) => ({ ...prev, trainerSignatureData: null }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.immatriculation.trim())
      newErrors.immatriculation = "L'immatriculation est requise";
    if (!formData.clientName.trim())
      newErrors.clientName = "Le nom du client est requis";
    if (!formData.workDescription.trim())
      newErrors.workDescription = "La description des travaux est requise";
    if (!formData.trainerId)
      newErrors.trainerId = "Le formateur responsable est requis";

    // ✅ V8.3 : Validation photos supprimée - gérée par ReceptionForm

    if (interventionMode === "simple") {
      formData.apprentices.forEach((apprentice, index) => {
        if (!apprentice.name.trim()) {
          newErrors[`apprentice_${index}_name`] =
            "Le nom de l'apprenant est requis";
        }
        if (!apprentice.groupe) {
          newErrors[`apprentice_${index}_groupe`] = "Le groupe est requis";
        }
        if (apprentice.competences.length === 0) {
          newErrors[`apprentice_${index}_competences`] =
            "Sélectionnez au moins une compétence";
        }

        // Vérifier signature: canvas OU signature existante
        const sigRef = apprenticeSignatureRefs.current[index];
        const hasNewSignature = sigRef?.current && !sigRef.current.isEmpty();
        const hasExistingSignature = !!signatures.apprentices?.[index];

        if (!hasNewSignature && !hasExistingSignature) {
          newErrors[`apprentice_${index}_signature`] =
            "La signature est requise";
        }
      });

      if (!formData.date_debut) {
        newErrors.date_debut = "La date d'arrivée du véhicule est obligatoire";
      }
      if (!formData.date_fin) {
        newErrors.date_fin = "La date de sortie prévue est obligatoire";
      }
    }

    if (interventionMode === "multi") {
      if (sessions.length === 0) {
        newErrors.sessions = "Au moins une session est requise";
      }

      sessions.forEach((session, sIndex) => {
        if (!session.date_session) {
          newErrors[`session_${sIndex}_date`] =
            "La date de la session est requise";
        }

        if (
          !session.apprenants_presents ||
          session.apprenants_presents.length === 0
        ) {
          newErrors[`session_${sIndex}_apprenants`] =
            "Au moins un apprenant est requis";
        } else {
          session.apprenants_presents.forEach((app, aIndex) => {
            if (!app.name) {
              newErrors[`session_${sIndex}_apprenant_${aIndex}_name`] =
                "Le nom est requis";
            }
          });
        }
      });

      if (!formData.date_debut_prevue) {
        newErrors.date_debut_prevue = "La date de début prévue est obligatoire";
      }
      if (!formData.date_fin_prevue) {
        newErrors.date_fin_prevue = "La date de fin prévue est obligatoire";
      }
    }

    // Vérifier signature formateur
    const hasNewTrainerSig =
      trainerSignatureRef.current && !trainerSignatureRef.current.isEmpty();
    const hasExistingTrainerSig =
      !!signatures.trainer || !!formData.trainerSignatureData;

    if (!hasNewTrainerSig && !hasExistingTrainerSig) {
      newErrors.trainerSignature = "La signature du formateur est requise";
    }

    if (!formData.checklist.some((item) => item.checked)) {
      newErrors.checklist = "Au moins un contrôle doit être effectué";
    }

    if (
      interventionMode === "simple" &&
      formData.date_debut &&
      formData.date_fin &&
      formData.date_debut_heure &&
      formData.date_fin_heure
    ) {
      const debut = new Date(
        `${formData.date_debut}T${formData.date_debut_heure.replace(/^(\d{2}:\d{2}).*/, "$1:00")}`
      );
      const fin = new Date(
        `${formData.date_fin}T${formData.date_fin_heure.replace(/^(\d{2}:\d{2}).*/, "$1:00")}`
      );

      if (fin < debut) {
        newErrors.date_fin =
          "La date de sortie doit être après la date d'arrivée";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const generatePdf = async () => {
    if (pdfRef.current) {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      console.log("📄 Génération du PDF...");

      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      console.log("📐 Taille canvas:", canvas.width, "x", canvas.height, "px");

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      console.log("📏 Taille image calculée:", imgWidth, "x", imgHeight, "mm");
      console.log("📄 Pages nécessaires:", Math.ceil(imgHeight / pageHeight));

      let heightLeft = imgHeight;
      let position = 0;
      let page = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        page++;
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        console.log(`📄 Page ${page + 1} ajoutée`);
      }

      console.log("✅ PDF généré avec", page + 1, "page(s)");
      return pdf;
    }
  };

  // ✅ Fonction uploadPhotos améliorée (évite les doublons)
  const uploadPhotos = async () => {
    // ✅ V8.3 : Photos gérées par ReceptionForm, on ne les upload PAS ici
    if (receptionData && receptionData.photos) {
      console.log('📸 [V8.3] Photos dans réception, pas d\'upload:', receptionData.photos);
      return {}; // Vide, car dans receptions.photos
    }
    
    if (isEditing && formData.photos) {
      // Mode édition ancien format (avant V8.3) : garde photos existantes
      console.log('📸 [V8.3] Mode édition ancien format, garde photos');
      return formData.photos || {};
    }
    
    console.log('⚠️ [V8.3] Aucune photo (normal si réception existe)');
    
    const photoUrls = {};

    const uploadPhoto = async (photoType, photoData) => {
      if (!photoData) return null;

      console.log(`🔍 uploadPhoto ${photoType}, type:`, typeof photoData);

      if (typeof photoData === "object" && photoData !== null) {
        if (photoData.path) {
          console.log(`📷 Photo ${photoType}: existe déjà (path), skip upload`);
          return photoData.path;
        }
        if (photoData.publicUrl) {
          const urlParts = photoData.publicUrl.split("/");
          const fileName = urlParts[urlParts.length - 1];
          console.log(
            `📷 Photo ${photoType}: existe déjà (publicUrl), skip upload`
          );
          return fileName;
        }
      }

      if (typeof photoData === "string") {
        // URL Supabase existante (signée ou publique)
        if (
          photoData.includes("supabase") ||
          photoData.includes("vehicle-photos")
        ) {
          const urlParts = photoData.split("/");
          const fileName = urlParts[urlParts.length - 1].split("?")[0];
          console.log(
            `📷 Photo ${photoType}: URL Supabase existante, skip upload`
          );
          return fileName;
        }

        if (!photoData.startsWith("data:") && !photoData.startsWith("http")) {
          console.log(
            `📷 Photo ${photoType}: nom de fichier existant, skip upload`
          );
          return photoData;
        }

        if (photoData.startsWith("data:")) {
          console.log(
            `📷 Photo ${photoType}: base64 détecté, upload nécessaire`
          );

          try {
            const response = await fetch(photoData);
            const blob = await response.blob();

            const fileName = `${formData.immatriculation.replace(/\s+/g, "_")}_${photoType}_${Date.now()}.jpg`;

            const { data, error } = await supabase.storage
              .from("vehicle-photos")
              .upload(fileName, blob, {
                contentType: "image/jpeg",
                upsert: true,
              });

            if (error) {
              console.error(`❌ Erreur upload photo ${photoType}:`, error);
              return null;
            }

            console.log(`✅ Photo ${photoType} uploadée: ${fileName}`);
            return fileName;
          } catch (err) {
            console.error(`❌ Erreur fetch/upload photo ${photoType}:`, err);
            return null;
          }
        }
      }

      return null;
    };

    for (const [photoType, photoData] of Object.entries(photos)) {
      if (photoData) {
        const fileName = await uploadPhoto(photoType, photoData);
        if (fileName) {
          photoUrls[photoType] = fileName;
        }
      }
    }

    console.log("📷 URLs photos finales:", photoUrls);
    return photoUrls;
  };

  // ✅ FONCTION saveToSupabase CORRIGÉE - Capture directe des signatures
  const saveToSupabase = async (
    pdfFile,
    preCapturedTrainerSig = null,
    preCapturedApprenticeSigs = []
  ) => {
    console.log("💾 saveToSupabase appelé - État:", {
      isEditing,
      editingInterventionId,
      mode: isEditing ? "MODIFICATION" : "CRÉATION",
      hasTrainer: !!preCapturedTrainerSig,
      apprentices: preCapturedApprenticeSigs.map(
        (s, i) => `#${i + 1}: ${s ? "OK" : "null"}`
      ),
    });

    try {
      setSaving(true);

      const photoUrls = await uploadPhotos();

      // ✅ UTILISER LES SIGNATURES PRÉ-CAPTURÉES (passées en paramètre)
      console.log("📝 Utilisation des signatures pré-capturées...");

      const trainerSignatureBase64 = preCapturedTrainerSig;
      const apprenticeSignaturesBase64 = preCapturedApprenticeSigs;

      console.log("📋 Signatures reçues:", {
        formateur: !!trainerSignatureBase64,
        apprentis: apprenticeSignaturesBase64.map(
          (s, i) => `#${i + 1}: ${s ? "OK" : "null"}`
        ),
      });

      console.log("📋 Résumé signatures capturées:", {
        formateur: trainerSignatureBase64 ? "OK" : "null",
        apprentis: apprenticeSignaturesBase64.map(
          (s, i) => `#${i + 1}: ${s ? "OK" : "null"}`
        ),
      });

      // ✅ Upload des signatures apprentis
      const apprenticeSignatures = [];

      for (let i = 0; i < formData.apprentices.length; i++) {
        let signatureData = null;
        const sigBase64 = apprenticeSignaturesBase64[i];

        if (sigBase64) {
          // Cas 1: Objet avec fileName (signature existante non modifiée)
          if (typeof sigBase64 === "object" && sigBase64.fileName) {
            signatureData = sigBase64;
            console.log(
              `🔄 Signature apprenti #${i + 1}: objet existant conservé`
            );
          }
          // Cas 2: URL signée existante, extraire le fileName
          else if (
            typeof sigBase64 === "string" &&
            (sigBase64.includes("supabase") || sigBase64.includes("token="))
          ) {
            const urlParts = sigBase64.split("/");
            const fileName = urlParts[urlParts.length - 1].split("?")[0];
            signatureData = { fileName: fileName };
            console.log(
              `🔄 Signature apprenti #${i + 1}: URL existante -> fileName: ${fileName}`
            );
          }
          // Cas 3: Base64 nouvelle, uploader
          else if (
            typeof sigBase64 === "string" &&
            sigBase64.startsWith("data:")
          ) {
            console.log(`📤 Upload nouvelle signature apprenti #${i + 1}...`);

            try {
              const res = await fetch(sigBase64);
              const blob = await res.blob();
              const fileName = `${formData.immatriculation.replace(/\s+/g, "_")}_apprentice_${i + 1}_${Date.now()}.png`;

              const { data, error } = await supabase.storage
                .from("signatures")
                .upload(fileName, blob, {
                  contentType: "image/png",
                  upsert: true,
                });

              if (error) {
                console.error(
                  `❌ Erreur upload signature apprenti #${i + 1}:`,
                  error
                );
              } else {
                signatureData = { fileName: fileName };
                console.log(
                  `✅ Signature apprenti #${i + 1} uploadée: ${fileName}`
                );
              }
            } catch (err) {
              console.error(
                `❌ Exception upload signature apprenti #${i + 1}:`,
                err
              );
            }
          }
        }

        apprenticeSignatures.push(signatureData);
      }

      // ✅ Upload signature formateur
      let trainerSignatureData = null;

      if (trainerSignatureBase64) {
        // Cas 1: Objet avec fileName
        if (
          typeof trainerSignatureBase64 === "object" &&
          trainerSignatureBase64.fileName
        ) {
          trainerSignatureData = trainerSignatureBase64;
          console.log("🔄 Signature formateur: objet existant conservé");
        }
        // Cas 2: URL signée existante
        else if (
          typeof trainerSignatureBase64 === "string" &&
          (trainerSignatureBase64.includes("supabase") ||
            trainerSignatureBase64.includes("token="))
        ) {
          const urlParts = trainerSignatureBase64.split("/");
          const fileName = urlParts[urlParts.length - 1].split("?")[0];
          trainerSignatureData = { fileName: fileName };
          console.log(
            `🔄 Signature formateur: URL existante -> fileName: ${fileName}`
          );
        }
        // Cas 3: Base64 nouvelle, uploader
        else if (
          typeof trainerSignatureBase64 === "string" &&
          trainerSignatureBase64.startsWith("data:")
        ) {
          console.log("📤 Upload nouvelle signature formateur...");

          try {
            const res = await fetch(trainerSignatureBase64);
            const blob = await res.blob();
            const fileName = `${formData.immatriculation.replace(/\s+/g, "_")}_trainer_${formData.trainerId}_${Date.now()}.png`;

            const { data, error } = await supabase.storage
              .from("signatures")
              .upload(fileName, blob, {
                contentType: "image/png",
                upsert: true,
              });

            if (error) {
              console.error("❌ Erreur upload signature formateur:", error);
            } else {
              trainerSignatureData = { fileName: fileName };
              console.log(`✅ Signature formateur uploadée: ${fileName}`);
            }
          } catch (err) {
            console.error("❌ Exception upload signature formateur:", err);
          }
        }
      }

      console.log("📊 Résultat upload signatures:", {
        formateur: {
          uploadé: !!trainerSignatureData,
          fileName: trainerSignatureData?.fileName,
        },
        apprentis: apprenticeSignatures.map((sig, i) => ({
          index: i + 1,
          uploadé: !!sig,
          fileName: sig?.fileName,
        })),
      });

      // ✅ SUPPRESSION DE L'ANCIEN PDF SI EN MODE ÉDITION
      if (isEditing && editingInterventionId) {
        try {
          const { data: oldIntervention } = await supabase
            .from("interventions")
            .select("report_file")
            .eq("id", editingInterventionId)
            .single();

          if (oldIntervention?.report_file?.fileName) {
            const oldFileName = oldIntervention.report_file.fileName;
            console.log(`🗑️ Suppression de l'ancien PDF: ${oldFileName}`);

            const { error: deleteError } = await supabase.storage
              .from("interventions-pdf")
              .remove([oldFileName]);

            if (deleteError) {
              console.warn("⚠️ Erreur suppression ancien PDF:", deleteError);
            } else {
              console.log("✅ Ancien PDF supprimé");
            }
          }
        } catch (err) {
          console.warn("⚠️ Impossible de supprimer l'ancien PDF:", err);
        }
      }

      // Upload du PDF
      const pdfFileName = `${formData.immatriculation}_intervention_${Date.now()}.pdf`;
      const pdfBlob = pdfFile.output("blob");

      const { data: pdfData, error: pdfError } = await supabase.storage
        .from("interventions-pdf")
        .upload(pdfFileName, pdfBlob, { upsert: true });

      if (pdfError) throw pdfError;

      // Préparer les compétences
      let allCompetences = [];

      if (interventionMode === "simple") {
        allCompetences = formData.apprentices.flatMap((apprentice) =>
          apprentice.competences.map((comp) => ({
            ...comp,
            apprenticeName: apprentice.name,
          }))
        );
      } else {
        sessions.forEach((session) => {
          (session.apprenants_presents || []).forEach((apprenant) => {
            (apprenant.competences || []).forEach((comp) => {
              allCompetences.push({
                ...comp,
                apprenticeName: apprenant.name,
                sessionId: session.id,
                sessionDate: session.date_session,
              });
            });
          });
        });
      }

      // Calculer durée totale
      let dureeTotaleMinutes = 0;
      if (interventionMode === "multi") {
        sessions.forEach((session) => {
          if (session.heure_debut && session.heure_fin) {
            const [startH, startM] = session.heure_debut.split(":").map(Number);
            const [endH, endM] = session.heure_fin.split(":").map(Number);
            let diffMinutes = endH * 60 + endM - (startH * 60 + startM);
            if (diffMinutes < 0) diffMinutes += 24 * 60;
            dureeTotaleMinutes += diffMinutes;
          }
        });
      }

      // Préparer les timestamps
      const dateDebutTimestamp =
        interventionMode === "simple" ?
          formData.date_debut && formData.date_debut_heure ?
            `${formData.date_debut}T${formData.date_debut_heure.replace(/^(\d{2}:\d{2}).*/, "$1:00")}`
          : null
        : formData.date_debut_prevue ? `${formData.date_debut_prevue}T08:00:00`
        : null;

      const dateFinTimestamp =
        interventionMode === "simple" ?
          formData.date_fin && formData.date_fin_heure ?
            `${formData.date_fin}T${formData.date_fin_heure.replace(/^(\d{2}:\d{2}).*/, "$1:00")}`
          : null
        : formData.date_fin_prevue ? `${formData.date_fin_prevue}T17:00:00`
        : null;

      // ✅ Préparer les données de l'intervention avec les signatures
      const interventionData = {
        client_id: formData.clientId,
        immatriculation: formData.immatriculation,
        client_name: formData.clientName,
        intervention_type: formData.interventionType,
        work_description: formData.workDescription,
        future_maintenance: formData.futureMaintenance || null,
        trainer_id: parseInt(formData.trainerId),
        completion_date: formData.completionDate,
        checklist_done: formData.checklist
          .filter((item) => item.checked)
          .map((item) => item.id),
        apprentices:
          interventionMode === "simple" ?
            formData.apprentices.map((apprentice, index) => ({
              name: apprentice.name,
              groupe: apprentice.groupe,
              groupe_name:
                groupes.find((g) => g.id === apprentice.groupe)?.name ||
                apprentice.groupe,
              apprenant_id: apprentice.apprenantId,
              signature: apprenticeSignatures[index], // ✅ Utilise la signature uploadée
              competences: apprentice.competences,
            }))
          : [],
        date_debut: dateDebutTimestamp,
        date_fin: dateFinTimestamp,
        date_debut_heure:
          interventionMode === "simple" ? formData.date_debut_heure : "08:00",
        date_fin_heure:
          interventionMode === "simple" ? formData.date_fin_heure : "17:00",
        trainer_signature: trainerSignatureData, // ✅ Utilise la signature uploadée
        trainer_name:
          trainers.find((t) => t.id.toString() === formData.trainerId)?.name ||
          formData.trainerName ||
          "Non assigné",
        report_file: {
          fileName: pdfFileName,
          uploadedAt: new Date().toISOString(),
        },
        photos: photoUrls,
        competences_travaillees: allCompetences,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: formData.status || "planifiee",
        is_multi_session: interventionMode === "multi",
        avancement: formData.avancement || 0,
        nombre_sessions: interventionMode === "multi" ? sessions.length : 1,
        duree_totale_minutes: dureeTotaleMinutes || 0,
        date_debut_prevue: formData.date_debut_prevue || null,
        date_fin_prevue: formData.date_fin_prevue || null,
      };

      let interventionResult;
      let insertError;

      if (isEditing && editingInterventionId) {
        console.log("🔄 Mise à jour intervention:", editingInterventionId);

        const { data, error } = await supabase
          .from("interventions")
          .update(interventionData)
          .eq("id", editingInterventionId)
          .select()
          .single();

        interventionResult = data;
        insertError = error;

        if (!error) {
          console.log(
            "✅ Intervention mise à jour avec succès:",
            editingInterventionId
          );
        }
      } else {
        console.log("➕ Création nouvelle intervention");

        const { data, error } = await supabase
          .from("interventions")
          .insert([interventionData])
          .select()
          .single();

        interventionResult = data;
        insertError = error;

        if (!error) {
          console.log("✅ Nouvelle intervention créée:", data.id);
        }
      }

      if (insertError) {
        console.error("❌ Erreur SQL:", insertError);
        throw insertError;
      }

      // Enregistrer les sessions si mode multi
      if (interventionMode === "multi" && sessions.length > 0) {
        for (const session of sessions) {
          const sessionData = {
            intervention_id: interventionResult.id,
            date_session: session.date_session,
            heure_debut: session.heure_debut,
            heure_fin: session.heure_fin,
            duree_minutes: (() => {
              if (!session.heure_debut || !session.heure_fin) return 0;
              const [startH, startM] = session.heure_debut
                .split(":")
                .map(Number);
              const [endH, endM] = session.heure_fin.split(":").map(Number);
              let diff = endH * 60 + endM - (startH * 60 + startM);
              return diff < 0 ? diff + 24 * 60 : diff;
            })(),
            numero_session: session.numero_session,
            travaux_realises: session.travaux_realises,
            observations: session.observations,
            formateur_id: session.formateur_id || null,
            formateur_name: session.formateur_name || null,
            apprenants_presents: session.apprenants_presents || [],
            competences_session: (session.apprenants_presents || []).flatMap(
              (a) => a.competences || []
            ),
            status: session.status || "terminee",
          };

          const { error: sessionError } = await supabase
            .from("intervention_sessions")
            .insert([sessionData]);

          if (sessionError) {
            console.error(
              "Erreur lors de l'enregistrement de la session:",
              sessionError
            );
          }
        }
      }

      // Enregistrer les compétences
      // ✅ CORRECTION: Si mode édition, supprimer les anciennes compétences d'abord
      if (isEditing && editingInterventionId) {
        console.log(
          "🗑️ Suppression des compétences existantes pour intervention:",
          editingInterventionId
        );
        const { error: deleteError } = await supabase
          .from("intervention_competences")
          .delete()
          .eq("intervention_id", editingInterventionId);

        if (deleteError) {
          console.error("❌ Erreur suppression compétences:", deleteError);
        } else {
          console.log("✅ Compétences existantes supprimées");
        }
      }

      // Puis INSERT les nouvelles compétences
      if (interventionMode === "simple") {
        console.log(
          "📝 Insertion nouvelles compétences et niveaux acquisition..."
        );

        for (const apprentice of formData.apprentices) {
          const apprenantId = apprentice.apprenantId;

          for (const comp of apprentice.competences) {
            // ✅ INSERT intervention_competences avec apprenant_id
            const { data: compData, error: compError } = await supabase
              .from("intervention_competences")
              .insert({
                intervention_id: interventionResult.id,
                pole_id: comp.pole_id,
                bloc_id: comp.bloc_id,
                activite_id: comp.activite_id,
                tache_id: comp.tache_id,
                apprenant_id: apprenantId, // ✅ NOUVEAU
              })
              .select()
              .single();

            if (compError) {
              console.error("❌ Erreur insertion compétence:", compError);
              continue;
            }

            // ⚠️ DÉSACTIVÉ TEMPORAIREMENT V8.1 - Problème competence_id (UUID vs INTEGER)
            // La table niveau_acquisition attend competence_id INTEGER, pas UUID
            // TODO: Trouver la correspondance tache_id (UUID) → competence_id (INTEGER)

            console.log(
              `ℹ️ [V8.1] Compétence insérée, niveau_acquisition désactivé temporairement`
            );

            /*
            // ✅ Vérifier si niveau_acquisition existe déjà
            const { data: existingNiveau } = await supabase
              .from("niveau_acquisition")
              .select("*")
              .eq("apprenant_id", apprenantId)
              .eq("competence_id", comp.tache_id)
              .single();

            if (existingNiveau) {
              console.log(`ℹ️ Niveau déjà existant pour apprenti ${apprenantId}, tâche ${comp.tache_id}`);
              
              // ✅ Mettre à jour la date d'évaluation
              await supabase
                .from("niveau_acquisition")
                .update({
                  date_evaluation: formData.completionDate || new Date().toISOString().split('T')[0],
                  evaluateur_id: formData.trainerId,
                  updated_at: new Date().toISOString(),
                })
                .eq("id", existingNiveau.id);
              
              console.log(`✅ Niveau acquisition mis à jour (date) pour apprenti ${apprenantId}, tâche ${comp.tache_id}`);
              
              // Si en mode édition et niveau différent, créer historique
              if (isEditing && existingNiveau.niveau !== (comp.niveau || "découverte")) {
                await supabase.from("historique_niveau_acquisition").insert({
                  niveau_acquisition_id: existingNiveau.id,
                  apprenant_id: apprenantId,
                  competence_id: comp.tache_id,
                  ancien_niveau: existingNiveau.niveau,
                  nouveau_niveau: comp.niveau || "découverte",
                  modifie_par: formData.trainerId,
                  motif_changement: "Mise à jour lors de l'intervention",
                });
              }
            } else {
              // ✅ Créer niveau_acquisition
              const { data: niveauData, error: niveauError } = await supabase
                .from("niveau_acquisition")
                .insert({
                  apprenant_id: apprenantId,
                  competence_id: comp.tache_id, // La tâche = compétence atomique
                  niveau: comp.niveau || "découverte", // ✅ V8: Utilise le niveau sélectionné
                  date_evaluation: formData.completionDate || new Date().toISOString().split('T')[0],
                  evaluateur_id: formData.trainerId,
                  commentaire: null,
                })
                .select()
                .single();

              if (niveauError) {
                console.error("❌ Erreur insertion niveau_acquisition:", niveauError);
              } else {
                console.log(`✅ Niveau acquisition créé pour apprenti ${apprenantId}, tâche ${comp.tache_id}, niveau: ${comp.niveau || "découverte"}`);
              }
            }
            */
          }
        }
        console.log(
          "✅ Nouvelles compétences insérées (niveau_acquisition désactivé temporairement)"
        );
      } else {
        console.log(
          "📝 Mode multi-session : Insertion compétences et niveaux..."
        );

        for (const session of sessions) {
          for (const apprenant of session.apprenants_presents || []) {
            const apprenantId = apprenant.apprenantId;

            for (const comp of apprenant.competences || []) {
              // ✅ INSERT intervention_competences
              const { data: compData, error: compError } = await supabase
                .from("intervention_competences")
                .insert({
                  intervention_id: interventionResult.id,
                  pole_id: comp.pole_id,
                  bloc_id: comp.bloc_id,
                  activite_id: comp.activite_id,
                  tache_id: comp.tache_id,
                  apprenant_id: apprenantId,
                })
                .select()
                .single();

              if (compError) {
                console.error("❌ Erreur insertion compétence:", compError);
                continue;
              }

              // ⚠️ DÉSACTIVÉ TEMPORAIREMENT V8.1 - Problème competence_id (UUID vs INTEGER)
              console.log(
                `ℹ️ [V8.1] Compétence insérée, niveau_acquisition désactivé temporairement`
              );

              /*
              // ✅ Vérifier si niveau_acquisition existe déjà
              const { data: existingNiveau } = await supabase
                .from("niveau_acquisition")
                .select("*")
                .eq("apprenant_id", apprenantId)
                .eq("competence_id", comp.tache_id)
                .single();

              if (existingNiveau) {
                console.log(`ℹ️ Niveau déjà existant pour apprenti ${apprenantId}, tâche ${comp.tache_id}`);
                
                // ✅ Mettre à jour la date d'évaluation
                await supabase
                  .from("niveau_acquisition")
                  .update({
                    date_evaluation: formData.completionDate || new Date().toISOString().split('T')[0],
                    evaluateur_id: formData.trainerId,
                    updated_at: new Date().toISOString(),
                  })
                  .eq("id", existingNiveau.id);
                
                console.log(`✅ Niveau acquisition mis à jour (date) pour apprenti ${apprenantId}, tâche ${comp.tache_id}`);
                
                // Si en mode édition et niveau différent, créer historique
                if (isEditing && existingNiveau.niveau !== (comp.niveau || "découverte")) {
                  await supabase.from("historique_niveau_acquisition").insert({
                    niveau_acquisition_id: existingNiveau.id,
                    apprenant_id: apprenantId,
                    competence_id: comp.tache_id,
                    ancien_niveau: existingNiveau.niveau,
                    nouveau_niveau: comp.niveau || "découverte",
                    modifie_par: formData.trainerId,
                    motif_changement: "Mise à jour lors de l'intervention multi-session",
                  });
                }
              } else {
                // ✅ Créer niveau_acquisition
                const { data: niveauData, error: niveauError } = await supabase
                  .from("niveau_acquisition")
                  .insert({
                    apprenant_id: apprenantId,
                    competence_id: comp.tache_id,
                    niveau: comp.niveau || "découverte", // ✅ V8: Utilise le niveau sélectionné
                    date_evaluation: formData.completionDate || new Date().toISOString().split('T')[0],
                    evaluateur_id: formData.trainerId,
                    commentaire: null,
                  })
                  .select()
                  .single();

                if (niveauError) {
                  console.error("❌ Erreur insertion niveau_acquisition:", niveauError);
                } else {
                  console.log(`✅ Niveau acquisition créé pour apprenti ${apprenantId}, tâche ${comp.tache_id}, niveau: ${comp.niveau || "découverte"}`);
                }
              }
              */
            }
          }
        }
        console.log(
          "✅ Compétences multi-session insérées (niveau_acquisition désactivé temporairement)"
        );
      }

      setSuccess(true);

      setTimeout(() => {
        resetForm();
        setSuccess(false);
        navigate("/pole-auto/interventions-list"); // ✅ CORRECTION: Route liste interventions
      }, 2000);

      return true;
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error);
      setErrors({
        submit:
          "Une erreur est survenue lors de l'enregistrement. Veuillez réessayer.",
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      controls.start({
        x: [0, -10, 10, -10, 0],
        transition: { duration: 0.5 },
      });

      const firstErrorKey = Object.keys(errors)[0];
      const firstErrorElement = document.querySelector(
        `[data-error="${firstErrorKey}"]`
      );
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      return;
    }

    if (previewMode) {
      const pdf = await generatePdf();
      await saveToSupabase(pdf);
    } else {
      setPreviewMode(true);
    }
  };

  const handleDirectSave = async () => {
    // 🔍 DEBUG: Vérifier état édition
    console.log("🎯 handleDirectSave appelé - État:", {
      isEditing,
      editingInterventionId,
      mode: isEditing ? "MODIFICATION" : "CRÉATION",
    });

    // ✅ V8.3 : Vérifier réception complète pour nouvelle intervention
    if (!isEditing && !receptionCompleted) {
      alert('⚠️ Veuillez d\'abord enregistrer la réception du véhicule (Étape 1)');
      
      // Scroll vers section réception
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      setShowReceptionForm(true);
      return;
    }

    if (!validateForm()) {
      controls.start({
        x: [0, -10, 10, -10, 0],
        transition: { duration: 0.5 },
      });

      const firstErrorKey = Object.keys(errors)[0];
      const firstErrorElement = document.querySelector(
        `[data-error="${firstErrorKey}"]`
      );
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      return;
    }

    // ✅ CRITIQUE: Capturer les signatures MAINTENANT, AVANT setPreviewMode(true)
    console.log("📝 CAPTURE DES SIGNATURES (formulaire encore visible)...");

    // Attendre un peu que les refs soient prêts
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Capturer signature formateur
    const capturedTrainerSig =
      trainerSignatureRef.current && !trainerSignatureRef.current.isEmpty() ?
        trainerSignatureRef.current.toDataURL()
      : signatures.trainer || formData.trainerSignatureData || null;

    console.log(
      "📝 Signature formateur capturée:",
      capturedTrainerSig ?
        `OK (${capturedTrainerSig.substring(0, 50)}...)`
      : "null"
    );

    // Capturer signatures apprentis
    const capturedApprenticeSigs = [];
    for (let i = 0; i < formData.apprentices.length; i++) {
      const sigRef = apprenticeSignatureRefs.current[i];
      let sigData = null;

      console.log(`📝 Capture apprenti #${i + 1}:`, {
        hasRef: !!sigRef,
        hasCurrent: !!sigRef?.current,
        isEmpty: sigRef?.current ? sigRef.current.isEmpty() : "N/A",
      });

      // 1. Nouvelle signature dessinée ?
      if (sigRef?.current && !sigRef.current.isEmpty()) {
        sigData = sigRef.current.toDataURL();
        console.log(
          `✅ Signature apprenti #${i + 1}: nouvelle depuis canvas (${sigData.substring(0, 50)}...)`
        );
      }
      // 2. Signature existante ?
      else if (signatures.apprentices?.[i]) {
        sigData = signatures.apprentices[i];
        console.log(
          `✅ Signature apprenti #${i + 1}: existante depuis état (${sigData.substring(0, 50)}...)`
        );
      }
      // 3. Signature dans formData ?
      else if (formData.apprentices[i]?.signature) {
        sigData = formData.apprentices[i].signature;
        console.log(
          `✅ Signature apprenti #${i + 1}: depuis formData (${sigData.substring(0, 50)}...)`
        );
      } else {
        console.warn(`⚠️ Pas de signature pour apprenti #${i + 1}`);
      }

      capturedApprenticeSigs[i] = sigData;
    }

    console.log("📋 SIGNATURES CAPTURÉES (formulaire visible):", {
      formateur: !!capturedTrainerSig,
      apprentis: capturedApprenticeSigs.map(
        (s, i) => `#${i + 1}: ${s ? "OK" : "null"}`
      ),
    });

    // ✅ MAINTENANT on peut passer en mode preview
    setPreviewMode(true);

    console.log("⏱️ Attente de 2 secondes pour que le DOM se charge...");
    setTimeout(async () => {
      console.log("📄 Génération du PDF maintenant...");
      const pdf = await generatePdf();

      // Passer les signatures capturées à saveToSupabase
      const success = await saveToSupabase(
        pdf,
        capturedTrainerSig,
        capturedApprenticeSigs
      );

      if (!success) {
        setPreviewMode(false);
      }
    }, 2000);
  };

  const resetForm = () => {
    setFormData({
      immatriculation: "",
      clientName: "",
      clientId: null,
      interventionType: "mechanical",
      workDescription: "",
      futureMaintenance: "",
      trainerId: "",
      completionDate: new Date().toISOString().split("T")[0],
      checklist: checklistItems.map((item) => ({ ...item, checked: false })),
      apprentices: [
        {
          id: 1,
          name: "",
          groupe: "",
          apprenantId: null,
          signature: null,
          competences: [],
        },
      ],
      photos: {
        frontBefore: null,
        rightBefore: null,
        leftBefore: null,
        backBefore: null,
        frontAfter: null,
        rightAfter: null,
        leftAfter: null,
        backAfter: null,
      },
      date_debut: "",
      date_debut_heure: "08:00",
      date_fin: "",
      date_fin_heure: "17:00",
      status: "planifiee",
      avancement: 0,
      is_multi_session: false,
      date_debut_prevue: "",
      date_fin_prevue: "",
      trainerSignatureData: null,
    });
    setPhotos({
      frontBefore: null,
      rightBefore: null,
      leftBefore: null,
      backBefore: null,
      frontAfter: null,
      rightAfter: null,
      leftAfter: null,
      backAfter: null,
    });
    setSignatures({ trainer: null, apprentices: [] });

    if (trainerSignatureRef.current) {
      trainerSignatureRef.current.clear();
    }
    apprenticeSignatureRefs.current.forEach((ref) => {
      if (ref && ref.current) {
        ref.current.clear();
      }
    });

    setPreviewMode(false);
    setSearchPlate("");
    setSearchError("");
    setInterventionMode("simple");
    setSessions([]);
    setExpandedSessions({});
    setIsEditing(false);
    setEditingInterventionId(null);
  };

  const handleCancel = () => {
    if (previewMode) {
      setPreviewMode(false);
    } else {
      navigate(-1);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Non spécifié";

    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR");
  };

  const formatDateTime = (dateString, heureString) => {
    if (!dateString) return "Non spécifié";

    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });

    return heureString ? `${formattedDate} à ${heureString}` : formattedDate;
  };

  // ============================================
  // RENDU DU COMPOSANT
  // ============================================

  if (showClientsList) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className='min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white'>
        <div className='max-w-7xl mx-auto'>
          <div className='flex justify-between items-center mb-8'>
            <h1 className='text-3xl font-bold text-blue-400'>
              Sélection du Client Pédagogique
            </h1>
            <button
              onClick={() => setShowClientsList(false)}
              className='px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white flex items-center'>
              <X className='w-5 h-5 mr-2' />
              Fermer
            </button>
          </div>

          <div className='bg-slate-800 rounded-lg shadow-xl p-6'>
            <div className='mb-6'>
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400' />
                <input
                  type='text'
                  placeholder='Rechercher un client...'
                  className='w-full bg-slate-700 border border-slate-600 rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
                  onChange={(e) => {
                    const searchTerm = e.target.value.toLowerCase();
                    setAllClients((prevClients) =>
                      prevClients.map((client) => ({
                        ...client,
                        hidden:
                          !client.fullName.toLowerCase().includes(searchTerm) &&
                          !client.immatriculation
                            .toLowerCase()
                            .includes(searchTerm),
                      }))
                    );
                  }}
                />
              </div>
            </div>

            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-slate-700'>
                <thead>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider'>
                      Immatriculation
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider'>
                      Client
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-slate-800 divide-y divide-slate-700'>
                  {allClients.filter((client) => !client.hidden).length > 0 ?
                    allClients
                      .filter((client) => !client.hidden)
                      .map((client, index) => (
                        <tr
                          key={client.id}
                          className={
                            index % 2 === 0 ? "bg-slate-800" : "bg-slate-750"
                          }>
                          <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-white'>
                            {client.immatriculation}
                          </td>
                          <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-300'>
                            {client.fullName}
                          </td>
                          <td className='px-6 py-4 whitespace-nowrap text-sm font-medium'>
                            <button
                              onClick={() => loadClientById(client.id)}
                              className='text-blue-400 hover:text-blue-300'>
                              Sélectionner
                            </button>
                          </td>
                        </tr>
                      ))
                  : <tr>
                      <td
                        colSpan='3'
                        className='px-6 py-4 text-center text-gray-400'>
                        Aucun client trouvé
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white overflow-hidden'>
      <div className='fixed inset-0 z-0'>
        <CarScene />
      </div>

      <motion.div
        animate={logoControls}
        className='fixed top-40 right-6 w-24 h-24 md:w-36 md:h-36 z-10'>
        <img
          src='/images/9b6b737e-c7d2-40e5-8a92-8bbe017d0fa9.png'
          alt='Logiciel Garage CFA'
          className='w-full h-full object-contain'
        />
      </motion.div>

      <div className='max-w-6xl mx-auto relative z-10'>
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}>
          <h1 className='text-3xl md:text-4xl font-bold mb-2 text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500'>
            {previewMode ?
              "Vérification de l'intervention"
            : isEditing ?
              "Modifier l'Intervention Pédagogique"
            : "Nouvelle Intervention Pédagogique"}
          </h1>
          <p className='text-gray-300 text-center mb-10'>
            {previewMode ?
              "Vérifiez les informations avant de finaliser l'enregistrement"
            : "Enregistrez les détails de l'intervention pédagogique réalisée sur un véhicule"
            }
          </p>
        </motion.div>

        {!previewMode && (
          <>
            {/* Sélection du mode d'intervention */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className='bg-slate-800/80 backdrop-blur-sm rounded-xl shadow-xl p-6 mb-8'>
              <div className='flex items-center mb-4'>
                <Layers className='w-6 h-6 text-blue-400 mr-2' />
                <h2 className='text-xl font-semibold text-blue-400'>
                  Type d'intervention
                </h2>
              </div>
              <div className='p-0.5 bg-gradient-to-r from-blue-400 to-pink-500 mb-6'></div>

              <InterventionModeSelector
                mode={interventionMode}
                onModeChange={handleModeChange}
              />
            </motion.div>

            {/* ================================ */}
            {/* ✅ V8.3 : SECTION RÉCEPTION      */}
            {/* ================================ */}
            {!previewMode && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className='mb-8'>
                
                {/* En-tête section réception */}
                <div className='bg-gradient-to-r from-purple-900/40 to-blue-900/40 backdrop-blur-sm rounded-xl shadow-xl p-6 mb-4 border border-purple-500/30'>
                  <div className='flex items-center justify-between flex-wrap gap-4'>
                    <div className='flex items-center gap-3'>
                      <div className='p-2 bg-purple-500/20 rounded-lg'>
                        <Car className='w-6 h-6 text-purple-400' />
                      </div>
                      <div>
                        <h2 className='text-xl font-semibold text-purple-400 flex items-center gap-2'>
                          Réception du Véhicule
                          {receptionCompleted && (
                            <span className='text-sm px-2 py-1 bg-green-500/20 text-green-400 rounded-full'>
                              ✓ Complète
                            </span>
                          )}
                        </h2>
                        <p className='text-sm text-gray-400 mt-1'>
                          {receptionCompleted ? 
                            'Réception enregistrée - Vous pouvez passer à l\'intervention'
                          : 'Étape 1 : Enregistrez la réception et les photos du véhicule'
                          }
                        </p>
                      </div>
                    </div>

                    {/* Boutons d\'action */}
                    <div className='flex items-center gap-3'>
                      {!receptionCompleted && (
                        <>
                          {/* ✅ Bouton rechercher réception existante */}
                          <button
                            type='button'
                            onClick={() => setShowReceptionSearch(true)}
                            className='px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all hover:scale-105 flex items-center gap-2 shadow-lg'>
                            <Search className='w-4 h-4' />
                            Rechercher réception
                          </button>

                          {/* Bouton nouvelle réception */}
                          <button
                            type='button'
                            onClick={() => setShowReceptionForm(!showReceptionForm)}
                            className='px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all hover:scale-105 flex items-center gap-2 shadow-lg'>
                            {showReceptionForm ? (
                              <>
                                <X className='w-4 h-4' />
                                Masquer
                              </>
                            ) : (
                              <>
                                <Camera className='w-4 h-4' />
                                {receptionData ? 'Modifier réception' : 'Nouvelle réception'}
                              </>
                            )}
                          </button>
                        </>
                      )}

                      {/* Info réception complète */}
                      {receptionCompleted && receptionData && (
                        <div className='flex items-center gap-4 bg-slate-800/50 px-4 py-2 rounded-lg'>
                          <div className='text-sm'>
                            <span className='text-gray-400'>📋 Immat:</span>{' '}
                            <span className='text-white font-medium'>{receptionData.immatriculation}</span>
                          </div>
                          <div className='text-sm'>
                            <span className='text-gray-400'>📅 Date:</span>{' '}
                            <span className='text-white font-medium'>
                              {new Date(receptionData.date_reception).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          <div className='text-sm'>
                            <span className='text-gray-400'>📸 Photos:</span>{' '}
                            <span className='text-green-400 font-medium'>
                              {Object.keys(receptionData.photos || {}).length}
                            </span>
                          </div>
                          <button
                            type='button'
                            onClick={() => {
                              setReceptionCompleted(false);
                              setShowReceptionForm(true);
                            }}
                            className='text-purple-400 hover:text-purple-300 flex items-center gap-1 transition-colors'>
                            <Edit3 className='w-4 h-4' />
                            Modifier
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Formulaire réception */}
                {showReceptionForm && !receptionCompleted && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className='bg-slate-800/80 backdrop-blur-sm rounded-xl shadow-xl overflow-hidden border border-purple-500/20 mb-6'>
                    <ReceptionForm
                      initialData={receptionData}
                      onReceptionSaved={(reception) => {
                        console.log('✅ [V8.3] Réception enregistrée:', reception);
                        
                        // Pré-rempli le formulaire intervention
                        setFormData(prev => ({
                          ...prev,
                          immatriculation: reception.immatriculation,
                          clientId: reception.client_id,
                          clientName: reception.client_name,
                          date_debut: reception.date_reception,
                          date_debut_heure: reception.heure_reception || '08:00',
                          reception_id: reception.id,
                        }));
                        
                        setReceptionData(reception);
                        setReceptionCompleted(true);
                        setShowReceptionForm(false);
                        setSearchPlate(reception.immatriculation);
                        
                        // Message succès
                        alert('✅ Réception enregistrée avec succès ! Vous pouvez maintenant remplir l\'intervention.');
                        
                        // Scroll vers section intervention
                        setTimeout(() => {
                          const interventionSection = document.querySelector('[data-section="intervention-details"]');
                          if (interventionSection) {
                            interventionSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }, 300);
                      }}
                      onCancel={() => {
                        setShowReceptionForm(false);
                      }}
                    />
                  </motion.div>
                )}

                {/* ✅ RECHERCHE RÉCEPTION EXISTANTE */}
                {showReceptionSearch && (
                  <ReceptionSearch
                    onReceptionSelected={(reception) => {
                      console.log('✅ [V8.3] Réception existante sélectionnée:', reception);
                      
                      // Pré-rempli le formulaire intervention
                      setFormData(prev => ({
                        ...prev,
                        immatriculation: reception.immatriculation,
                        clientId: reception.client_id,
                        clientName: reception.client_name,
                        date_debut: reception.date_reception,
                        date_debut_heure: reception.heure_reception || '08:00',
                        reception_id: reception.id,
                      }));
                      
                      setReceptionData(reception);
                      setReceptionCompleted(true);
                      setShowReceptionSearch(false);
                      setSearchPlate(reception.immatriculation);
                      
                      // Message succès
                      alert('✅ Réception existante chargée avec succès ! Vous pouvez maintenant remplir l\'intervention.');
                      
                      // Scroll vers section intervention
                      setTimeout(() => {
                        const interventionSection = document.querySelector('[data-section="intervention-details"]');
                        if (interventionSection) {
                          interventionSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }, 300);
                    }}
                    onClose={() => setShowReceptionSearch(false)}
                  />
                )}

                {/* Séparateur visuel si réception complète */}
                {receptionCompleted && (
                  <div className='flex items-center gap-4 my-8'>
                    <div className='flex-1 h-px bg-gradient-to-r from-transparent via-purple-500 to-blue-500'></div>
                    <div className='flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-900/50 to-blue-900/50 rounded-full border border-purple-500/30'>
                      <CheckCircle className='w-5 h-5 text-green-400' />
                      <span className='text-blue-400 font-semibold'>ÉTAPE 2 : INTERVENTION</span>
                    </div>
                    <div className='flex-1 h-px bg-gradient-to-r from-blue-500 via-purple-500 to-transparent'></div>
                  </div>
                )}
              </motion.div>
            )}
            {/* ================================ */}
            {/* FIN SECTION RÉCEPTION            */}
            {/* ================================ */}


            {/* Recherche client */}
            <motion.div
              data-section="intervention-details"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className='bg-slate-800/80 backdrop-blur-sm rounded-xl shadow-xl p-6 mb-8'>
              <div className='flex justify-between items-center mb-4'>
                <h2 className='text-xl font-semibold text-blue-400'>
                  Rechercher un client existant
                </h2>

                <div className='flex gap-2'>
                  <button
                    onClick={() => setShowClientsList(true)}
                    className='flex items-center text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg'>
                    <List className='w-4 h-4 mr-2' />
                    Liste des clients
                  </button>

                  <button
                    onClick={() => navigate("/pole-auto/interventions-list")} // ✅ CORRECTION: Route liste interventions
                    className='flex items-center text-white bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg'>
                    <List className='w-4 h-4 mr-2' />
                    Liste des interventions
                  </button>
                </div>
              </div>

              <form
                onSubmit={searchClientByPlate}
                className='flex flex-col md:flex-row gap-4'>
                <div className='flex-grow'>
                  <div className='relative'>
                    <Car className='absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400' />
                    <input
                      type='text'
                      value={searchPlate}
                      onChange={(e) =>
                        setSearchPlate(e.target.value.toUpperCase())
                      }
                      placeholder='Recherche par immatriculation'
                      className='w-full bg-slate-700 border border-slate-600 rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
                    />
                  </div>
                  {searchError && (
                    <p className='mt-2 text-sm text-red-400'>{searchError}</p>
                  )}
                </div>
                <button
                  type='submit'
                  disabled={isSearching}
                  className='bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors flex items-center justify-center'>
                  {isSearching ?
                    <>
                      <LoaderIcon className='animate-spin mr-2 h-5 w-5' />
                      Recherche...
                    </>
                  : <>
                      <Search className='mr-2 h-5 w-5' />
                      Rechercher
                    </>
                  }
                </button>
              </form>
            </motion.div>

          </>
        )}

        {previewMode && (
          <div className='mb-8 bg-white p-8 rounded-lg shadow-xl text-black'>
            <div ref={pdfRef} className='p-8'>
              <div className='flex flex-col items-center mb-8 gap-4'>
                <h2 className='text-2xl font-bold text-blue-900 text-center'>
                  Rapport d'Intervention Pédagogique
                </h2>
                <p className='text-gray-600 text-center'>
                  CFA de Montargis - Pôle Automobile
                </p>
                <img
                  src='/images/9b6b737e-c7d2-40e5-8a92-8bbe017d0fa9.png'
                  alt='Logo CFA Garage'
                  className='w-32 h-32 object-contain'
                />
                {interventionMode === "multi" && (
                  <div className='bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-medium'>
                    Intervention multi-sessions ({sessions.length} session
                    {sessions.length > 1 ? "s" : ""})
                  </div>
                )}
              </div>

              <div className='border-t border-b border-gray-300 py-4 my-4'>
                <h3 className='text-xl font-semibold mb-4 text-blue-800'>
                  Informations du véhicule
                </h3>
                <p>
                  <strong>Immatriculation:</strong> {formData.immatriculation}
                </p>
                <p>
                  <strong>Client:</strong> {formData.clientName}
                </p>
                <p>
                  <strong>Date d'intervention:</strong>{" "}
                  {formatDate(formData.completionDate)}
                </p>
                <p>
                  <strong>Type d'intervention:</strong>{" "}
                  {formData.interventionType === "mechanical" ?
                    "Mécanique"
                  : "Carrosserie"}
                </p>
                {interventionMode === "multi" && (
                  <>
                    <p>
                      <strong>Statut:</strong>{" "}
                      {formData.status === "planifiee" ?
                        "Planifiée"
                      : formData.status === "en_cours" ?
                        "En cours"
                      : formData.status === "terminee" ?
                        "Terminée"
                      : "Annulée"}
                    </p>
                    <p>
                      <strong>Avancement:</strong> {formData.avancement}%
                    </p>
                  </>
                )}
              </div>

              {/* Section des dates dans le PDF */}
              <div className='border-b border-gray-300 py-4 mb-4'>
                <h3 className='text-xl font-semibold mb-4 text-blue-800'>
                  Planification
                </h3>
                {interventionMode === "simple" ?
                  <>
                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <p>
                          <strong>Arrivée du véhicule:</strong>
                        </p>
                        <p className='text-gray-700'>
                          {formatDateTime(
                            formData.date_debut,
                            formData.date_debut_heure
                          )}
                        </p>
                      </div>
                      <div>
                        <p>
                          <strong>Sortie prévue:</strong>
                        </p>
                        <p className='text-gray-700'>
                          {formatDateTime(
                            formData.date_fin,
                            formData.date_fin_heure
                          )}
                        </p>
                      </div>
                    </div>
                    <p className='mt-2'>
                      <strong>Durée estimée:</strong>{" "}
                      {calculateDuration(
                        formData.date_debut,
                        formData.date_debut_heure,
                        formData.date_fin,
                        formData.date_fin_heure
                      )}
                    </p>
                  </>
                : <>
                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <p>
                          <strong>Date de début prévue:</strong>
                        </p>
                        <p className='text-gray-700'>
                          {formatDate(formData.date_debut_prevue)}
                        </p>
                      </div>
                      <div>
                        <p>
                          <strong>Date de fin prévue:</strong>
                        </p>
                        <p className='text-gray-700'>
                          {formatDate(formData.date_fin_prevue)}
                        </p>
                      </div>
                    </div>
                    <p className='mt-2'>
                      <strong>Nombre de sessions:</strong> {sessions.length}
                    </p>
                    <p>
                      <strong>Durée totale:</strong>{" "}
                      {calculateTotalSessionsDuration()}
                    </p>
                  </>
                }
              </div>

              {/* Sessions (mode multi) */}
              {interventionMode === "multi" && sessions.length > 0 && (
                <div className='border-b border-gray-300 py-4 mb-4'>
                  <h3 className='text-xl font-semibold mb-4 text-blue-800'>
                    Détail des sessions
                  </h3>
                  {sessions.map((session, idx) => (
                    <div
                      key={session.id}
                      className='mb-4 p-4 bg-gray-50 rounded-lg'>
                      <h4 className='font-semibold text-purple-700 mb-2'>
                        Session #{idx + 1} -{" "}
                        {session.date_session ?
                          formatDate(session.date_session)
                        : "Date non définie"}
                      </h4>
                      <p className='text-sm text-gray-600'>
                        <strong>Horaires:</strong> {session.heure_debut} -{" "}
                        {session.heure_fin}
                      </p>
                      {session.travaux_realises && (
                        <p className='text-sm text-gray-600 mt-1'>
                          <strong>Travaux:</strong> {session.travaux_realises}
                        </p>
                      )}
                      {session.apprenants_presents &&
                        session.apprenants_presents.length > 0 && (
                          <div className='mt-2'>
                            <p className='text-sm font-medium text-gray-700'>
                              Apprenants présents:
                            </p>
                            <ul className='list-disc list-inside text-sm text-gray-600'>
                              {session.apprenants_presents.map(
                                (app, appIdx) => (
                                  <li key={appIdx}>
                                    {app.name || "Non renseigné"}
                                    {app.groupe &&
                                      ` (${groupes.find((g) => g.id === app.groupe)?.name || app.groupe})`}
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}

              {Object.values(photos).some((photo) => photo !== null) && (
                <div className='border-b border-gray-300 py-4 mb-4'>
                  <h3 className='text-xl font-semibold mb-4 text-blue-800'>
                    Photos du véhicule
                  </h3>

                  {(photos.frontBefore ||
                    photos.rightBefore ||
                    photos.leftBefore ||
                    photos.backBefore) && (
                    <div className='mb-4'>
                      <h4 className='font-medium mb-2'>Avant intervention:</h4>
                      <div className='grid grid-cols-2 md:grid-cols-4 gap-2'>
                        {photos.frontBefore && (
                          <div>
                            <p className='text-xs text-gray-600 mb-1'>
                              Vue avant
                            </p>
                            <img
                              src={photos.frontBefore}
                              alt='Vue avant'
                              className='w-full h-auto border border-gray-300 rounded'
                              crossOrigin='anonymous'
                            />
                          </div>
                        )}
                        {photos.rightBefore && (
                          <div>
                            <p className='text-xs text-gray-600 mb-1'>
                              Vue droite
                            </p>
                            <img
                              src={photos.rightBefore}
                              alt='Vue droite'
                              className='w-full h-auto border border-gray-300 rounded'
                              crossOrigin='anonymous'
                            />
                          </div>
                        )}
                        {photos.leftBefore && (
                          <div>
                            <p className='text-xs text-gray-600 mb-1'>
                              Vue gauche
                            </p>
                            <img
                              src={photos.leftBefore}
                              alt='Vue gauche'
                              className='w-full h-auto border border-gray-300 rounded'
                              crossOrigin='anonymous'
                            />
                          </div>
                        )}
                        {photos.backBefore && (
                          <div>
                            <p className='text-xs text-gray-600 mb-1'>
                              Vue arrière
                            </p>
                            <img
                              src={photos.backBefore}
                              alt='Vue arrière'
                              className='w-full h-auto border border-gray-300 rounded'
                              crossOrigin='anonymous'
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {formData.interventionType === "bodywork" &&
                    (photos.frontAfter ||
                      photos.rightAfter ||
                      photos.leftAfter ||
                      photos.backAfter) && (
                      <div>
                        <h4 className='font-medium mb-2'>Après réparation:</h4>
                        <div className='grid grid-cols-2 md:grid-cols-4 gap-2'>
                          {photos.frontAfter && (
                            <div>
                              <p className='text-xs text-gray-600 mb-1'>
                                Vue avant
                              </p>
                              <img
                                src={photos.frontAfter}
                                alt='Vue avant (après)'
                                className='w-full h-auto border border-gray-300 rounded'
                                crossOrigin='anonymous'
                              />
                            </div>
                          )}
                          {photos.rightAfter && (
                            <div>
                              <p className='text-xs text-gray-600 mb-1'>
                                Vue droite
                              </p>
                              <img
                                src={photos.rightAfter}
                                alt='Vue droite (après)'
                                className='w-full h-auto border border-gray-300 rounded'
                                crossOrigin='anonymous'
                              />
                            </div>
                          )}
                          {photos.leftAfter && (
                            <div>
                              <p className='text-xs text-gray-600 mb-1'>
                                Vue gauche
                              </p>
                              <img
                                src={photos.leftAfter}
                                alt='Vue gauche (après)'
                                className='w-full h-auto border border-gray-300 rounded'
                                crossOrigin='anonymous'
                              />
                            </div>
                          )}
                          {photos.backAfter && (
                            <div>
                              <p className='text-xs text-gray-600 mb-1'>
                                Vue arrière
                              </p>
                              <img
                                src={photos.backAfter}
                                alt='Vue arrière (après)'
                                className='w-full h-auto border border-gray-300 rounded'
                                crossOrigin='anonymous'
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                </div>
              )}

              <div className='border-b border-gray-300 py-4 mb-4'>
                <h3 className='text-xl font-semibold mb-4 text-blue-800'>
                  Description des travaux réalisés
                </h3>
                <p className='whitespace-pre-line'>
                  {formData.workDescription}
                </p>
              </div>

              {/* Apprenants mode simple */}
              {interventionMode === "simple" && (
                <div className='border-b border-gray-300 py-4 mb-4'>
                  <h3 className='text-xl font-semibold mb-4 text-blue-800'>
                    Apprenants intervenants et compétences travaillées
                  </h3>
                  {formData.apprentices.map((apprentice, index) => {
                    const sigRef = apprenticeSignatureRefs.current[index];
                    const hasNewSignature =
                      sigRef?.current && !sigRef.current.isEmpty();
                    const existingSignature = signatures.apprentices?.[index];

                    return (
                      <div
                        key={index}
                        className='mb-6 pb-4 border-b border-gray-200 last:border-0'>
                        <div className='flex justify-between items-start mb-3'>
                          <div>
                            <p className='font-medium text-lg'>
                              {apprentice.name}
                            </p>
                            <p className='text-gray-600 text-sm'>
                              {groupes.find((g) => g.id === apprentice.groupe)
                                ?.name || apprentice.groupe}
                            </p>
                          </div>
                          <div>
                            {hasNewSignature ?
                              <div>
                                <p className='text-xs text-gray-600 mb-1'>
                                  Signature:
                                </p>
                                <img
                                  src={sigRef.current.toDataURL()}
                                  alt='Signature apprenant'
                                  className='h-12 border border-gray-300 rounded'
                                />
                              </div>
                            : existingSignature ?
                              <div>
                                <p className='text-xs text-gray-600 mb-1'>
                                  Signature:
                                </p>
                                <img
                                  src={existingSignature}
                                  alt='Signature apprenant'
                                  className='h-12 border border-gray-300 rounded'
                                  crossOrigin='anonymous'
                                />
                              </div>
                            : null}
                          </div>
                        </div>

                        {apprentice.competences &&
                          apprentice.competences.length > 0 && (
                            <div className='mt-3'>
                              <p className='text-sm font-medium text-gray-700 mb-2'>
                                Compétences travaillées:
                              </p>
                              <div className='bg-gray-50 p-3 rounded'>
                                {Object.entries(
                                  apprentice.competences.reduce((acc, comp) => {
                                    const poleKey = comp.pole_code || "AUTRE";
                                    if (!acc[poleKey]) {
                                      acc[poleKey] = {
                                        titre: comp.pole_titre || "Autre",
                                        items: [],
                                      };
                                    }
                                    acc[poleKey].items.push({
                                      code:
                                        comp.tache_code || comp.activite_code,
                                      titre:
                                        comp.tache_titre || comp.activite_titre,
                                    });
                                    return acc;
                                  }, {})
                                ).map(([code, data]) => (
                                  <div key={code} className='mb-2 last:mb-0'>
                                    <p className='text-sm font-medium text-blue-700'>
                                      {code} - {data.titre}
                                    </p>
                                    <ul className='ml-4 mt-1'>
                                      {data.items.map((item, idx) => (
                                        <li
                                          key={idx}
                                          className='text-xs text-gray-600'>
                                          • {item.code} - {item.titre}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className='border-b border-gray-300 py-4 mb-4'>
                <h3 className='text-xl font-semibold mb-4 text-blue-800'>
                  Contrôles effectués
                </h3>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  {formData.checklist.map((item) => (
                    <div key={item.id} className='flex items-center'>
                      <div
                        className={`w-5 h-5 mr-2 border rounded flex items-center justify-center ${item.checked ? "bg-green-500 border-green-600" : "bg-white border-gray-400"}`}>
                        {item.checked && (
                          <Check className='w-4 h-4 text-white' />
                        )}
                      </div>
                      <span>{item.title}</span>
                    </div>
                  ))}
                </div>
              </div>

              {formData.futureMaintenance && (
                <div className='border-b border-gray-300 py-4 mb-4'>
                  <h3 className='text-xl font-semibold mb-4 text-blue-800'>
                    Travaux à prévoir
                  </h3>
                  <p className='whitespace-pre-line'>
                    {formData.futureMaintenance}
                  </p>
                </div>
              )}

              <div className='py-4 mb-4'>
                <h3 className='text-xl font-semibold mb-4 text-blue-800'>
                  Validation du formateur
                </h3>
                <div className='flex flex-col md:flex-row justify-between items-start md:items-center border-t border-gray-200 pt-4'>
                  <div>
                    <p>
                      <strong>Formateur:</strong>{" "}
                      {trainers.find(
                        (t) => String(t.id) === String(formData.trainerId)
                      )?.name || "Non spécifié"}
                    </p>
                    <p>
                      <strong>Date:</strong>{" "}
                      {formatDate(formData.completionDate)}
                    </p>
                  </div>
                  <div className='mt-4 md:mt-0'>
                    <p className='mb-2 font-medium'>Signature:</p>
                    {(
                      trainerSignatureRef.current &&
                      !trainerSignatureRef.current.isEmpty()
                    ) ?
                      <img
                        src={trainerSignatureRef.current.toDataURL()}
                        alt='Signature formateur'
                        className='h-20 border border-gray-300 rounded p-1'
                      />
                    : signatures.trainer || formData.trainerSignatureData ?
                      <img
                        src={
                          signatures.trainer || formData.trainerSignatureData
                        }
                        alt='Signature formateur'
                        className='h-20 border border-gray-300 rounded p-1'
                        crossOrigin='anonymous'
                      />
                    : <div className='h-20 w-60 border border-red-500 rounded flex items-center justify-center text-red-500'>
                        Non signé
                      </div>
                    }
                  </div>
                </div>
              </div>

              <div className='text-right mt-8'>
                <p className='text-sm text-gray-600'>
                  Document généré le: {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {!previewMode && (
          <motion.form
            ref={formRef}
            onSubmit={handleSubmit}
            animate={controls}
            className='bg-slate-800/90 backdrop-blur-lg rounded-xl shadow-xl p-6 md:p-8'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6'>
              <div className='md:col-span-2'>
                <div className='flex items-center mb-4'>
                  <Car className='w-6 h-6 text-blue-400 mr-2' />
                  <h2 className='text-xl font-semibold text-blue-400'>
                    Informations du véhicule et du client
                  </h2>
                </div>
                <div className='p-0.5 bg-gradient-to-r from-blue-400 to-pink-500 mb-6'></div>
              </div>

              <div data-error='immatriculation'>
                <label className='block text-sm font-medium text-gray-300 mb-1'>
                  Immatriculation *
                </label>
                <input
                  type='text'
                  name='immatriculation'
                  value={formData.immatriculation}
                  onChange={handleChange}
                  placeholder='AA-123-BB'
                  className={`w-full bg-slate-700 border ${errors.immatriculation ? "border-red-500" : "border-slate-600"} rounded-lg p-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500`}
                  readOnly={!!formData.immatriculation}
                />
                {errors.immatriculation && (
                  <p className='mt-1 text-sm text-red-400'>
                    {errors.immatriculation}
                  </p>
                )}
              </div>

              <div data-error='clientName'>
                <label className='block text-sm font-medium text-gray-300 mb-1'>
                  Nom du client *
                </label>
                <input
                  type='text'
                  name='clientName'
                  value={formData.clientName}
                  onChange={handleChange}
                  placeholder='Nom du client'
                  className={`w-full bg-slate-700 border ${errors.clientName ? "border-red-500" : "border-slate-600"} rounded-lg p-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500`}
                  readOnly={!!formData.clientName}
                />
                {errors.clientName && (
                  <p className='mt-1 text-sm text-red-400'>
                    {errors.clientName}
                  </p>
                )}
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-300 mb-1'>
                  Date d'intervention
                </label>
                <input
                  type='date'
                  name='completionDate'
                  value={formData.completionDate}
                  onChange={handleChange}
                  className='w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-300 mb-1'>
                  Type d'intervention *
                </label>
                <div className='flex gap-4'>
                  <label className='flex items-center'>
                    <input
                      type='radio'
                      name='interventionType'
                      value='mechanical'
                      checked={formData.interventionType === "mechanical"}
                      onChange={handleChange}
                      className='mr-2'
                    />
                    <span className='text-white'>Mécanique</span>
                  </label>
                  <label className='flex items-center'>
                    <input
                      type='radio'
                      name='interventionType'
                      value='bodywork'
                      checked={formData.interventionType === "bodywork"}
                      onChange={handleChange}
                      className='mr-2'
                    />
                    <span className='text-white'>Carrosserie</span>
                  </label>
                </div>
              </div>

              {/* Section Statut et Avancement (mode multi uniquement) */}
              {interventionMode === "multi" && (
                <>
                  <div className='md:col-span-2 mt-6'>
                    <div className='flex items-center mb-4'>
                      <BarChart3 className='w-6 h-6 text-purple-400 mr-2' />
                      <h2 className='text-xl font-semibold text-purple-400'>
                        Statut et Avancement
                      </h2>
                    </div>
                    <div className='p-0.5 bg-gradient-to-r from-purple-400 to-pink-500 mb-6'></div>
                  </div>

                  <div className='md:col-span-2'>
                    <label className='block text-sm font-medium text-gray-300 mb-3'>
                      Statut de l'intervention
                    </label>
                    <StatusSelector
                      status={formData.status}
                      onStatusChange={(status) =>
                        setFormData((prev) => ({ ...prev, status }))
                      }
                    />
                  </div>

                  <div className='md:col-span-2 mt-4'>
                    <ProgressBar
                      value={formData.avancement}
                      onChange={(value) =>
                        setFormData((prev) => ({ ...prev, avancement: value }))
                      }
                    />
                  </div>
                </>
              )}

              {/* SECTION : Dates */}
              <div className='md:col-span-2 mt-6'>
                <div className='flex items-center mb-4'>
                  <Calendar className='w-6 h-6 text-blue-400 mr-2' />
                  <h2 className='text-xl font-semibold text-blue-400'>
                    {interventionMode === "simple" ?
                      "Planification de l'intervention"
                    : "Période de l'intervention"}
                  </h2>
                </div>
                <div className='p-0.5 bg-gradient-to-r from-blue-400 to-pink-500 mb-6'></div>
              </div>

              {interventionMode === "simple" ?
                <>
                  <div data-error='date_debut'>
                    <label className='block text-sm font-medium text-gray-300 mb-1'>
                      Date d'arrivée du véhicule *
                    </label>
                    <input
                      type='date'
                      name='date_debut'
                      value={formData.date_debut}
                      onChange={handleChange}
                      className={`w-full bg-slate-700 border ${errors.date_debut ? "border-red-500" : "border-slate-600"} rounded-lg p-3 text-white focus:outline-none focus:border-blue-500`}
                    />
                    {errors.date_debut && (
                      <p className='mt-1 text-sm text-red-400'>
                        {errors.date_debut}
                      </p>
                    )}
                  </div>

                  <div data-error='date_debut_heure'>
                    <label className='block text-sm font-medium text-gray-300 mb-1'>
                      Heure d'arrivée *
                    </label>
                    <div className='relative'>
                      <Clock className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                      <input
                        type='time'
                        name='date_debut_heure'
                        value={formData.date_debut_heure}
                        onChange={handleChange}
                        className={`w-full bg-slate-700 border ${errors.date_debut_heure ? "border-red-500" : "border-slate-600"} rounded-lg p-3 pl-10 text-white focus:outline-none focus:border-blue-500`}
                      />
                    </div>
                    {errors.date_debut_heure && (
                      <p className='mt-1 text-sm text-red-400'>
                        {errors.date_debut_heure}
                      </p>
                    )}
                  </div>

                  <div data-error='date_fin'>
                    <label className='block text-sm font-medium text-gray-300 mb-1'>
                      Date de sortie prévue *
                    </label>
                    <input
                      type='date'
                      name='date_fin'
                      value={formData.date_fin}
                      onChange={handleChange}
                      className={`w-full bg-slate-700 border ${errors.date_fin ? "border-red-500" : "border-slate-600"} rounded-lg p-3 text-white focus:outline-none focus:border-blue-500`}
                    />
                    {errors.date_fin && (
                      <p className='mt-1 text-sm text-red-400'>
                        {errors.date_fin}
                      </p>
                    )}
                  </div>

                  <div data-error='date_fin_heure'>
                    <label className='block text-sm font-medium text-gray-300 mb-1'>
                      Heure de sortie prévue *
                    </label>
                    <div className='relative'>
                      <Clock className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                      <input
                        type='time'
                        name='date_fin_heure'
                        value={formData.date_fin_heure}
                        onChange={handleChange}
                        className={`w-full bg-slate-700 border ${errors.date_fin_heure ? "border-red-500" : "border-slate-600"} rounded-lg p-3 pl-10 text-white focus:outline-none focus:border-blue-500`}
                      />
                    </div>
                    {errors.date_fin_heure && (
                      <p className='mt-1 text-sm text-red-400'>
                        {errors.date_fin_heure}
                      </p>
                    )}
                  </div>

                  {formData.date_debut && formData.date_fin && (
                    <div className='md:col-span-2'>
                      <div className='bg-blue-900/30 border border-blue-500/50 rounded-lg p-4 flex items-center'>
                        <Clock className='w-5 h-5 text-blue-400 mr-3' />
                        <div>
                          <p className='text-sm text-gray-400'>
                            Durée estimée de l'intervention
                          </p>
                          <p className='text-lg font-semibold text-blue-400'>
                            {calculateDuration(
                              formData.date_debut,
                              formData.date_debut_heure,
                              formData.date_fin,
                              formData.date_fin_heure
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              : <>
                  <div data-error='date_debut_prevue'>
                    <label className='block text-sm font-medium text-gray-300 mb-1'>
                      Date de début prévue *
                    </label>
                    <input
                      type='date'
                      name='date_debut_prevue'
                      value={formData.date_debut_prevue}
                      onChange={handleChange}
                      className={`w-full bg-slate-700 border ${errors.date_debut_prevue ? "border-red-500" : "border-slate-600"} rounded-lg p-3 text-white focus:outline-none focus:border-blue-500`}
                    />
                    {errors.date_debut_prevue && (
                      <p className='mt-1 text-sm text-red-400'>
                        {errors.date_debut_prevue}
                      </p>
                    )}
                  </div>

                  <div data-error='date_fin_prevue'>
                    <label className='block text-sm font-medium text-gray-300 mb-1'>
                      Date de fin prévue *
                    </label>
                    <input
                      type='date'
                      name='date_fin_prevue'
                      value={formData.date_fin_prevue}
                      onChange={handleChange}
                      className={`w-full bg-slate-700 border ${errors.date_fin_prevue ? "border-red-500" : "border-slate-600"} rounded-lg p-3 text-white focus:outline-none focus:border-blue-500`}
                    />
                    {errors.date_fin_prevue && (
                      <p className='mt-1 text-sm text-red-400'>
                        {errors.date_fin_prevue}
                      </p>
                    )}
                  </div>

                  {sessions.length > 0 && (
                    <div className='md:col-span-2'>
                      <div className='bg-purple-900/30 border border-purple-500/50 rounded-lg p-4'>
                        <div className='flex items-center justify-between'>
                          <div className='flex items-center'>
                            <History className='w-5 h-5 text-purple-400 mr-3' />
                            <div>
                              <p className='text-sm text-gray-400'>
                                Sessions planifiées
                              </p>
                              <p className='text-lg font-semibold text-purple-400'>
                                {sessions.length} session
                                {sessions.length > 1 ? "s" : ""} -{" "}
                                {calculateTotalSessionsDuration()} au total
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              }

              <div className='md:col-span-2 mt-6'>
                <div className='flex items-center mb-4'>
                  <Wrench className='w-6 h-6 text-blue-400 mr-2' />
                  <h2 className='text-xl font-semibold text-blue-400'>
                    Description des travaux
                  </h2>
                </div>
                <div className='p-0.5 bg-gradient-to-r from-blue-400 to-pink-500 mb-6'></div>
              </div>

              <div className='md:col-span-2' data-error='workDescription'>
                <label className='block text-sm font-medium text-gray-300 mb-1'>
                  Travaux réalisés *
                </label>
                <textarea
                  name='workDescription'
                  value={formData.workDescription}
                  onChange={handleChange}
                  placeholder='Décrivez les travaux réalisés sur le véhicule...'
                  rows={5}
                  className={`w-full bg-slate-700 border ${errors.workDescription ? "border-red-500" : "border-slate-600"} rounded-lg p-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500`}
                />
                {errors.workDescription && (
                  <p className='mt-1 text-sm text-red-400'>
                    {errors.workDescription}
                  </p>
                )}
              </div>

              {/* SECTION SESSIONS (mode multi) */}
              {interventionMode === "multi" && (
                <>
                  <div className='md:col-span-2 mt-6'>
                    <div className='flex items-center justify-between mb-4'>
                      <div className='flex items-center'>
                        <CalendarDays className='w-6 h-6 text-purple-400 mr-2' />
                        <h2 className='text-xl font-semibold text-purple-400'>
                          Sessions de travail
                        </h2>
                      </div>
                      <button
                        type='button'
                        onClick={addSession}
                        className='flex items-center text-white bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg transition-colors'>
                        <Plus className='w-4 h-4 mr-2' />
                        Ajouter une session
                      </button>
                    </div>
                    <div className='p-0.5 bg-gradient-to-r from-purple-400 to-pink-500 mb-6'></div>
                  </div>

                  <div className='md:col-span-2' data-error='sessions'>
                    {sessions.length === 0 ?
                      <div className='bg-slate-700/50 border border-dashed border-slate-500 rounded-lg p-8 text-center'>
                        <CalendarDays className='w-12 h-12 text-slate-500 mx-auto mb-4' />
                        <p className='text-gray-400 mb-4'>
                          Aucune session de travail ajoutée
                        </p>
                        <button
                          type='button'
                          onClick={addSession}
                          className='inline-flex items-center text-purple-400 hover:text-purple-300'>
                          <Plus className='w-5 h-5 mr-2' />
                          Ajouter la première session
                        </button>
                      </div>
                    : sessions.map((session, index) => (
                        <SessionForm
                          key={session.id}
                          session={session}
                          index={index}
                          onUpdate={updateSession}
                          onRemove={removeSession}
                          trainers={trainers}
                          groupes={groupes}
                          canRemove={sessions.length > 1}
                          isExpanded={expandedSessions[session.id] !== false}
                          onToggleExpand={() => toggleSessionExpand(session.id)}
                        />
                      ))
                    }
                    {errors.sessions && (
                      <p className='mt-2 text-sm text-red-400'>
                        {errors.sessions}
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* SECTION APPRENANTS (mode simple uniquement) */}
              {interventionMode === "simple" && (
                <>
                  <div className='md:col-span-2 mt-6'>
                    <div className='flex items-center mb-4'>
                      <User className='w-6 h-6 text-blue-400 mr-2' />
                      <h2 className='text-xl font-semibold text-blue-400'>
                        Apprenants intervenants
                      </h2>
                    </div>
                    <div className='p-0.5 bg-gradient-to-r from-blue-400 to-pink-500 mb-6'></div>
                  </div>

                  <div className='md:col-span-2'>
                    {formData.apprentices.map((apprentice, index) => (
                      <div
                        key={index}
                        className='mb-8 p-6 bg-slate-700/50 rounded-lg'>
                        <div className='flex justify-between items-center mb-4'>
                          <h3 className='text-lg font-bold text-blue-300'>
                            Apprenant #{index + 1}
                          </h3>
                          {formData.apprentices.length > 1 && (
                            <button
                              type='button'
                              onClick={() => removeApprentice(index)}
                              className='text-red-400 hover:text-red-300'>
                              <X className='w-5 h-5' />
                            </button>
                          )}
                        </div>

                        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
                          <div data-error={`apprentice_${index}_groupe`}>
                            <label className='block text-sm font-medium text-gray-300 mb-2'>
                              Groupe *
                            </label>
                            <select
                              value={apprentice.groupe}
                              onChange={(e) =>
                                handleApprenticeChange(
                                  index,
                                  "groupe",
                                  e.target.value
                                )
                              }
                              className={`w-full bg-slate-700 border ${errors[`apprentice_${index}_groupe`] ? "border-red-500" : "border-slate-600"} rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 appearance-none`}>
                              <option value=''>Sélectionner un groupe</option>
                              {groupes.map((groupe) => (
                                <option key={groupe.id} value={groupe.id}>
                                  {groupe.name}
                                </option>
                              ))}
                            </select>
                            {errors[`apprentice_${index}_groupe`] && (
                              <p className='mt-1 text-sm text-red-400'>
                                {errors[`apprentice_${index}_groupe`]}
                              </p>
                            )}
                          </div>

                          <div data-error={`apprentice_${index}_name`}>
                            <label className='block text-sm font-medium text-gray-300 mb-2'>
                              Nom et prénom *
                            </label>
                            <ApprenantSelector
                              groupe={apprentice.groupe}
                              selectedName={apprentice.name}
                              onSelect={(name, id) =>
                                handleApprenticeChange(index, "name", name, id)
                              }
                            />
                            {errors[`apprentice_${index}_name`] && (
                              <p className='mt-1 text-sm text-red-400'>
                                {errors[`apprentice_${index}_name`]}
                              </p>
                            )}
                          </div>
                        </div>

                        <div
                          className='mb-6'
                          data-error={`apprentice_${index}_competences`}>
                          <div className='flex items-center mb-3'>
                            <BookOpen className='w-5 h-5 text-blue-400 mr-2' />
                            <label className='block text-sm font-medium text-gray-300'>
                              Compétences travaillées *
                            </label>
                          </div>
                          <CompetenceSelector
                            typeFormation={
                              apprentice.groupe ?
                                groupes.find((g) => g.id === apprentice.groupe)
                                  ?.typeFormation
                              : null
                            }
                            selectedCompetences={apprentice.competences || []}
                            onCompetenceChange={(competences) =>
                              handleApprenticeCompetencesChange(
                                index,
                                competences
                              )
                            }
                          />
                          {errors[`apprentice_${index}_competences`] && (
                            <p className='mt-1 text-sm text-red-400'>
                              {errors[`apprentice_${index}_competences`]}
                            </p>
                          )}
                        </div>


                        {(() => {
                          console.log("🔍 DEBUG V8.2 - Apprenant:", apprentice);
                          console.log(
                            "🔍 DEBUG V8.2 - Compétences:",
                            apprentice.competences
                          );
                          return null;
                        })()}

                        {apprentice.competences &&
                          apprentice.competences.length > 0 && (
                            <div className='mt-4 p-4 bg-blue-900/20 rounded-lg border border-blue-700'>
                              <h4 className='text-sm font-semibold text-blue-300 mb-3 flex items-center gap-2'>
                                <BarChart3 className='w-4 h-4' />
                                Compétences sélectionnées (
                                {apprentice.competences.length})
                              </h4>
                              <div className='space-y-2'>
                                {apprentice.competences.map(
                                  (comp, compIndex) => (
                                    <div
                                      key={`${comp.tache_id || comp.activite_id}-${compIndex}`}
                                      className='flex items-center justify-between bg-slate-800 p-3 rounded-lg'>
                                      <div className='flex-1'>
                                        <p className='text-sm font-medium text-white'>
                                          {comp.tache_code ||
                                            comp.activite_code}{" "}
                                          -{" "}
                                          {comp.tache_titre ||
                                            comp.activite_titre}
                                        </p>
                                      </div>
                                      <div className='ml-4'>
                                        <select
                                          value={comp.niveau || "découverte"}
                                          onChange={(e) => {
                                            const updatedCompetences = [
                                              ...apprentice.competences,
                                            ];
                                            updatedCompetences[
                                              compIndex
                                            ].niveau = e.target.value;
                                            handleApprenticeCompetencesChange(
                                              index,
                                              updatedCompetences
                                            );
                                          }}
                                          className='px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm'>
                                          <option value='découverte'>
                                            🔵 Découverte
                                          </option>
                                          <option value='application'>
                                            🟢 Application
                                          </option>
                                          <option value='maîtrise'>
                                            🟡 Maîtrise
                                          </option>
                                          <option value='expert'>
                                            🔴 Expert
                                          </option>
                                        </select>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                        {/* Afficher signature APPRENANT existante en mode édition */}
                        {isEditing &&
                          (signatures.apprentices?.[index] ||
                            formData.apprentices[index]?.signature) && (
                            <div className='mb-4 p-4 bg-slate-700/50 rounded-lg'>
                              <p className='text-sm text-gray-300 mb-2 flex items-center'>
                                <CheckCircle className='w-4 h-4 mr-2 text-green-400' />
                                Signature existante :
                              </p>
                              <img
                                src={
                                  signatures.apprentices?.[index] ||
                                  ((
                                    typeof formData.apprentices[index]
                                      ?.signature === "string"
                                  ) ?
                                    formData.apprentices[index].signature
                                  : null)
                                }
                                alt={`Signature apprenant ${index + 1} existante`}
                                className='max-w-xs border border-gray-600 rounded bg-white p-2'
                                crossOrigin='anonymous'
                                onError={(e) => {
                                  console.error(
                                    `❌ Erreur chargement signature apprenant ${index + 1}:`,
                                    signatures.apprentices[index]
                                  );
                                  e.target.style.display = "none";
                                }}
                                onLoad={() =>
                                  console.log(
                                    `✅ Signature apprenant ${index + 1} affichée avec succès`
                                  )
                                }
                              />
                              <p className='text-xs text-gray-400 mt-2 italic'>
                                💡 Pour modifier, dessinez une nouvelle
                                signature ci-dessous
                              </p>
                            </div>
                          )}

                        {/* Zone de signature apprenant */}
                        <div data-error={`apprentice_${index}_signature`}>
                          <label className='block text-sm font-medium text-gray-300 mb-2'>
                            Signature de l'apprenant *
                          </label>
                          <div
                            className={`border ${errors[`apprentice_${index}_signature`] ? "border-red-500" : "border-slate-600"} rounded-lg overflow-hidden`}>
                            <OptimizedSignaturePad
                              ref={(el) => {
                                if (!apprenticeSignatureRefs.current[index]) {
                                  apprenticeSignatureRefs.current[index] = {
                                    current: null,
                                  };
                                }
                                apprenticeSignatureRefs.current[index].current =
                                  el;
                              }}
                              penColor='black'
                              backgroundColor='white'
                            />
                          </div>
                          <div className='flex justify-between mt-2'>
                            <button
                              type='button'
                              onClick={() => clearApprenticeSignature(index)}
                              className='text-sm text-gray-400 hover:text-white'>
                              Effacer la signature
                            </button>
                            <p className='text-sm text-gray-400'>
                              Signez dans la zone ci-dessus
                            </p>
                          </div>
                          {errors[`apprentice_${index}_signature`] && (
                            <p className='mt-1 text-sm text-red-400'>
                              {errors[`apprentice_${index}_signature`]}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}

                    <button
                      type='button'
                      onClick={addApprentice}
                      className='mt-2 flex items-center text-blue-400 hover:text-blue-300 bg-slate-700/30 p-3 rounded-lg'>
                      <UserPlus className='w-5 h-5 mr-2' />
                      Ajouter un apprenant
                    </button>
                  </div>
                </>
              )}

              <div className='md:col-span-2 mt-6'>
                <div className='flex items-center mb-4'>
                  <Clipboard className='w-6 h-6 text-blue-400 mr-2' />
                  <h2 className='text-xl font-semibold text-blue-400'>
                    Contrôles effectués
                  </h2>
                </div>
                <div className='p-0.5 bg-gradient-to-r from-blue-400 to-pink-500 mb-6'></div>
              </div>

              <div className='md:col-span-2' data-error='checklist'>
                <div className='bg-slate-700/50 p-6 rounded-lg mb-4'>
                  <p className='mb-3 text-gray-300 text-sm'>
                    Cochez les contrôles qui ont été effectués:
                  </p>
                  <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3'>
                    {formData.checklist.map((item) => (
                      <div key={item.id} className='flex items-start'>
                        <div className='flex items-center h-5'>
                          <input
                            id={`check-${item.id}`}
                            type='checkbox'
                            checked={item.checked}
                            onChange={() => handleChecklistChange(item.id)}
                            className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-500 rounded'
                          />
                        </div>
                        <label
                          htmlFor={`check-${item.id}`}
                          className='ml-3 text-sm text-gray-300'>
                          {item.title}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                {errors.checklist && (
                  <p className='mt-1 text-sm text-red-400'>
                    {errors.checklist}
                  </p>
                )}
              </div>

              <div className='md:col-span-2 mt-6'>
                <div className='flex items-center mb-4'>
                  <Wrench className='w-6 h-6 text-blue-400 mr-2' />
                  <h2 className='text-xl font-semibold text-blue-400'>
                    Travaux à prévoir
                  </h2>
                </div>
                <div className='p-0.5 bg-gradient-to-r from-blue-400 to-pink-500 mb-6'></div>
              </div>

              <div className='md:col-span-2'>
                <label className='block text-sm font-medium text-gray-300 mb-1'>
                  Travaux ou entretiens à prévoir (optionnel)
                </label>
                <textarea
                  name='futureMaintenance'
                  value={formData.futureMaintenance}
                  onChange={handleChange}
                  placeholder='Décrivez les travaux à prévoir sur le véhicule...'
                  rows={3}
                  className='w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500'
                />
              </div>

              <div className='md:col-span-2 mt-6'>
                <div className='flex items-center mb-4'>
                  <CheckSquare className='w-6 h-6 text-blue-400 mr-2' />
                  <h2 className='text-xl font-semibold text-blue-400'>
                    Validation du formateur
                  </h2>
                </div>
                <div className='p-0.5 bg-gradient-to-r from-blue-400 to-pink-500 mb-6'></div>
              </div>

              <div data-error='trainerId'>
                <label className='block text-sm font-medium text-gray-300 mb-2'>
                  Formateur responsable *
                </label>
                <div className='relative'>
                  <select
                    name='trainerId'
                    value={formData.trainerId}
                    onChange={handleChange}
                    className={`w-full bg-slate-700 border ${errors.trainerId ? "border-red-500" : "border-slate-600"} rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 appearance-none`}>
                    <option value=''>Sélectionner un formateur</option>
                    {trainers.map((trainer) => (
                      <option key={trainer.id} value={trainer.id}>
                        {trainer.name}
                      </option>
                    ))}
                  </select>
                  <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400'>
                    <svg
                      className='h-5 w-5'
                      fill='currentColor'
                      viewBox='0 0 20 20'>
                      <path
                        fillRule='evenodd'
                        d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                        clipRule='evenodd'></path>
                    </svg>
                  </div>
                </div>
                {errors.trainerId && (
                  <p className='mt-1 text-sm text-red-400'>
                    {errors.trainerId}
                  </p>
                )}
              </div>

              {/* Signature du formateur */}
              <div className='md:col-span-2' data-error='trainerSignature'>
                <label className='block text-sm font-medium text-gray-300 mb-2'>
                  Signature du formateur *
                </label>

                {/* Afficher signature FORMATEUR existante en mode édition */}
                {isEditing &&
                  (signatures.trainer || formData.trainerSignatureData) && (
                    <div className='mb-4 p-4 bg-slate-700/50 rounded-lg'>
                      <p className='text-sm text-gray-300 mb-2 flex items-center'>
                        <CheckCircle className='w-4 h-4 mr-2 text-green-400' />
                        Signature existante :
                      </p>
                      <img
                        src={
                          signatures.trainer || formData.trainerSignatureData
                        }
                        alt='Signature formateur existante'
                        className='max-w-xs border border-gray-600 rounded bg-white p-2'
                        crossOrigin='anonymous'
                        onError={(e) => {
                          console.error(
                            "❌ Erreur chargement signature formateur"
                          );
                          e.target.style.display = "none";
                        }}
                        onLoad={() =>
                          console.log(
                            "✅ Signature formateur chargée avec succès"
                          )
                        }
                      />
                      <p className='text-xs text-gray-400 mt-2 italic'>
                        💡 Pour modifier, dessinez une nouvelle signature
                        ci-dessous
                      </p>
                    </div>
                  )}

                <div
                  className={`border ${errors.trainerSignature ? "border-red-500" : "border-slate-600"} rounded-lg overflow-hidden`}>
                  <OptimizedSignaturePad
                    ref={trainerSignatureRef}
                    penColor='black'
                    backgroundColor='white'
                  />
                </div>
                <div className='flex justify-between mt-2'>
                  <button
                    type='button'
                    onClick={clearTrainerSignature}
                    className='text-sm text-gray-400 hover:text-white'>
                    Effacer la signature
                  </button>
                  <p className='text-sm text-gray-400'>
                    Signez dans la zone ci-dessus
                  </p>
                </div>
                {errors.trainerSignature && (
                  <p className='mt-1 text-sm text-red-400'>
                    {errors.trainerSignature}
                  </p>
                )}
              </div>
            </div>

            {errors.submit && (
              <div className='mt-6 p-4 bg-red-500/20 border border-red-500 rounded-lg'>
                <p className='text-red-400'>{errors.submit}</p>
              </div>
            )}
          </motion.form>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className='flex flex-col sm:flex-row justify-center gap-4 mt-8'>
          <motion.button
            type='button'
            onClick={handleCancel}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className='px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-2'>
            {previewMode ?
              <>
                <ArrowLeft className='w-5 h-5 mr-2' />
                <span>Modifier</span>
              </>
            : <>
                <X className='w-5 h-5 mr-2' />
                <span>Annuler</span>
              </>
            }
          </motion.button>

          {!previewMode && (
            <motion.button
              type='button'
              onClick={handleDirectSave}
              disabled={saving}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className='px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center space-x-2'>
              <Save className='w-5 h-5 mr-2' />
              <span>Enregistrer</span>
            </motion.button>
          )}

          <motion.button
            type='button'
            onClick={handleSubmit}
            disabled={saving}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-6 py-3 ${saving || success ? "bg-green-600" : "bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900"} text-white rounded-lg transition-colors flex items-center justify-center space-x-2`}>
            {saving ?
              <>
                <svg
                  className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'>
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                </svg>
                <span>Enregistrement...</span>
              </>
            : success ?
              <>
                <CheckCircle className='w-5 h-5 mr-2' />
                <span>Enregistré avec succès!</span>
              </>
            : <>
                {previewMode ?
                  <>
                    <Save className='w-5 h-5 mr-2' />
                    <span>Enregistrer</span>
                  </>
                : <>
                    <Eye className='w-5 h-5 mr-2' />
                    <span>Prévisualiser</span>
                  </>
                }
              </>
            }
          </motion.button>
        </motion.div>
      </div>

      <Loader />
    </motion.div>
  );
};

export default InterventionForm;